import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { contractsAPI, paymentsAPI, vehiclesAPI, driversAPI, attachmentsAPI, auditAPI, bankAccountsAPI, cardTerminalsAPI, usersAPI, vehicleTypesAPI, crmClientsAPI } from "../lib/api";
import { formatDate, formatVehicleLabel, isPostServiceByDate } from "../lib/formatters";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ClientSelector from "../components/ClientSelector";
import { PaymentLinkDialog } from "../components/PaymentLinkDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

// Build version for deployment verification
const COMPONENT_VERSION = "4.12.2"; // FEAT: CRM Client integration in contract detail

import {
  MapPin,
  Clock,
  Users,
  DollarSign,
  Truck,
  User,
  FileText,
  CreditCard,
  Paperclip,
  History,
  ArrowLeft,
  Circle,
  Plus,
  Upload,
  Download,
  ExternalLink,
  Plane,
  Briefcase,
  Check,
  AlertCircle,
  Loader2,
  Building2,
  Smartphone,
  Edit,
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  BookOpen,
  UserCheck,
  FileDown,
  X,
  Route,
  Trash2,
  Eye,
  FileEdit,
  ChevronDown,
  ChevronRight,
  Info,
  Undo2,
  Link2 as Link2Icon
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import MultipleAssignments from "../components/MultipleAssignments";
import { Component } from "react";

// Error Boundary para secciones individuales
class SectionErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || "Error desconocido" };
  }

  componentDidCatch(error, info) {
    console.error(`[SectionErrorBoundary] Error in ${this.props.sectionName || "section"}:`, error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm font-medium">Error al cargar {this.props.sectionName || "esta sección"}</p>
          </div>
          <p className="text-xs text-red-600 mt-1">
            {this.state.errorMessage}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Helper function to normalize extras field for API
// Backend expects: extras: List[Dict[str, Any]] (array of objects)
// Frontend input sets it as a float/number, so we need to convert
const normalizeExtrasForAPI = (extras, originalExtras) => {
  // If extras is undefined, don't include it in payload
  if (extras === undefined) return undefined;
  
  // If extras is already an array, validate and return
  if (Array.isArray(extras)) {
    return extras.length > 0 ? extras : undefined;
  }
  
  // If extras is a number (from the input field), convert to array format
  const extrasValue = parseFloat(extras) || 0;
  if (extrasValue > 0) {
    // Create a single "Extras" entry with the total amount
    return [{ name: "Extras", amount: extrasValue }];
  }
  
  // If original contract had extras and user set to 0, clear them
  if (originalExtras && Array.isArray(originalExtras) && originalExtras.length > 0 && extrasValue === 0) {
    return []; // Empty array to clear extras
  }
  
  // No extras to send
  return undefined;
};

// Helper function to safely extract error message from API responses
const extractErrorMessage = (error, defaultMsg = "Error desconocido") => {
  const detail = error?.response?.data?.detail;
  
  if (!detail) return defaultMsg;
  
  // FastAPI validation errors come as array
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(", ");
  }
  
  // Single validation error object
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  
  // String message
  return String(detail);
};

// Error Boundary GLOBAL para todo el componente
class GlobalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "", errorStack: "" };
  }

  static getDerivedStateFromError(error) {
    return { 
      hasError: true, 
      errorMessage: error?.message || "Error desconocido",
      errorStack: error?.stack || ""
    };
  }

  componentDidCatch(error, info) {
    console.error("[CONTRATO DETAIL GLOBAL ERROR]", error, info);
    // Log para debugging
    console.error("[ERROR STACK]", error?.stack);
    console.error("[COMPONENT STACK]", info?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Layout>
          <div className="p-8">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />
                  <div className="space-y-3">
                    <h2 className="text-lg font-semibold text-red-800">
                      Error al cargar el contrato
                    </h2>
                    <p className="text-sm text-red-700">
                      Ocurrió un error inesperado al mostrar este contrato. 
                      Por favor intente nuevamente o contacte a soporte técnico.
                    </p>
                    <p className="text-xs text-red-600 font-mono bg-red-100 p-2 rounded">
                      {this.state.errorMessage}
                    </p>
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        onClick={() => window.location.reload()}
                        className="border-red-300 text-red-700 hover:bg-red-100"
                      >
                        Recargar página
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => window.history.back()}
                        className="border-red-300 text-red-700 hover:bg-red-100"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Volver
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </Layout>
      );
    }
    return this.props.children;
  }
}

// Componente interno con toda la lógica
function ContratoDetalleContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isLogistica, canManageAssignments } = useAuth();
  
  // Main contract state
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  
  // Section-specific states with isolated error handling
  const [payments, setPayments] = useState([]);
  const [paymentsError, setPaymentsError] = useState(null);
  
  const [attachments, setAttachments] = useState([]);
  const [attachmentsError, setAttachmentsError] = useState(null);
  
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditError, setAuditError] = useState(null);
  
  const [changeLogs, setChangeLogs] = useState([]);
  const [changeLogsError, setChangeLogsError] = useState(null);
  
  // Render error state (for catching unexpected render crashes)
  const [renderError, setRenderError] = useState(null);
  
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  // v4.56.0: listas completas (incluye inactivas) para resolver históricos
  const [vehiclesAll, setVehiclesAll] = useState([]);
  const [driversAll, setDriversAll] = useState([]);
  
  // Traceability data
  const [createdByUser, setCreatedByUser] = useState(null);
  const [salesOwnerUser, setSalesOwnerUser] = useState(null);
  // v4.59.0: indicador si existen asignaciones v2 (para banner amarillo en Resumen)
  const [hasV2Assignments, setHasV2Assignments] = useState(false);
  // v4.59.3: ids de líneas con asignación v2 activa (para bloquear edición de ventana operativa)
  const [activeV2LineIds, setActiveV2LineIds] = useState([]);
  // v4.59.3: control de expand/collapse de la intención operativa por línea en edición
  const [expandedEditLineIds, setExpandedEditLineIds] = useState([]);
  
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  // Eliminación de pagos (admin only) — v4.49.0
  const [deletePaymentDialogOpen, setDeletePaymentDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletePaymentReason, setDeletePaymentReason] = useState("");
  const [deletePaymentSubmitting, setDeletePaymentSubmitting] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [trafficDialogOpen, setTrafficDialogOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({ 
    amount: 0, 
    method: "EFECTIVO", 
    reference: "", 
    notes: "",
    bank_account_id: "",
    terminal_id: ""
  });
  const [assignmentData, setAssignmentData] = useState({ vehicle_id: "", driver_id: "", traffic_light: "" });
  const [newTrafficLight, setNewTrafficLight] = useState("");

  // Post-service modification state (mirrors backend is_post_service)
  const [assignmentReason, setAssignmentReason] = useState("");
  const [trafficReason, setTrafficReason] = useState("");
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cardTerminals, setCardTerminals] = useState([]);
  
  // Deposit warning state
  const [depositInsufficient, setDepositInsufficient] = useState(false);
  const [depositShortfall, setDepositShortfall] = useState(0);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [changeReasonDialogOpen, setChangeReasonDialogOpen] = useState(false);
  const [changeReason, setChangeReason] = useState("");
  const [pendingChanges, setPendingChanges] = useState(null);
  const [hasPayments, setHasPayments] = useState(false);
  
  // Logistics confirmation modal
  const [logisticsConfirmOpen, setLogisticsConfirmOpen] = useState(false);
  const [operationalChangeReason, setOperationalChangeReason] = useState("");
  const [hasLogisticsAssignment, setHasLogisticsAssignment] = useState(false);
  
  // PDF generation state
  const [pdfLoading, setPdfLoading] = useState(false);
  
  // Service orders by unit
  const [serviceOrders, setServiceOrders] = useState([]);
  const [serviceOrdersDialogOpen, setServiceOrdersDialogOpen] = useState(false);
  const [serviceOrdersLoading, setServiceOrdersLoading] = useState(false);
  
  // Reassign contract modal (Admin only)
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignData, setReassignData] = useState({ new_sales_owner_id: "", reason: "" });
  const [allUsers, setAllUsers] = useState([]);
  const [reassignLoading, setReassignLoading] = useState(false);

  // Complete contract modal
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completionNote, setCompletionNote] = useState("");
  
  // P2: Contractual client data editing
  const [clientEditDialogOpen, setClientEditDialogOpen] = useState(false);
  const [clientEditData, setClientEditData] = useState({});
  const [clientEditReason, setClientEditReason] = useState("");
  const [clientEditLoading, setClientEditLoading] = useState(false);
  const [clientEditHistory, setClientEditHistory] = useState([]);
  const [showClientEditHistory, setShowClientEditHistory] = useState(false);
  
  // Vehicle types for editing vehicle_lines
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [newVehicleLine, setNewVehicleLine] = useState({ type: "", quantity: 1, capacity: 4, base_price: 0 });
  
  // Check if user can edit this contract
  // Admin/admin_vendedor: full edit
  // Logistica/vendedor_logistica: operational edit (no pricing)
  // Vendedor: only their own contracts
  const canEditContract = contract && (
    isAdmin || 
    isLogistica ||
    (
      (contract.sales_owner_id === user?.id || contract.created_by_user_id === user?.id) &&
      !["completado", "cancelado"].includes(contract.status)
    )
  );
  
  // Commercial editing (pricing fields) — restricted to admin + owner
  const canEditPricing = contract && (
    isAdmin || 
    contract.sales_owner_id === user?.id || 
    contract.created_by_user_id === user?.id
  );

  // Fase 1: Owner-vendedor con edición LIMITADA
  // Solo origen, destino, fecha/hora, itinerario. Sin pricing ni vehicle_lines.
  const isOwnerLimited = (
    user?.role === "vendedor"
    && contract
    && (contract.sales_owner_id === user?.id || contract.created_by_user_id === user?.id)
    && !isAdmin
  );
  const isOwnerBlockedByStatus = isOwnerLimited && contract && ["completado", "cancelado"].includes(contract.status);
  const OWNER_ALLOWED_FIELDS = ["origin", "destination", "pickup_datetime", "end_datetime", "full_itinerary"];

  const applyOwnerLimitedFilter = (payload) => {
    if (!isOwnerLimited) return payload;
    const ownerAllowed = new Set([
      ...OWNER_ALLOWED_FIELDS,
      "change_reason",
      "operational_change_reason",
      "post_service_change_reason",
    ]);
    Object.keys(payload).forEach((k) => { if (!ownerAllowed.has(k)) delete payload[k]; });
    if (!payload.change_reason) payload.change_reason = (changeReason || "").trim();
    // operational_change_reason is auto-derived in backend from change_reason for owners
    if (isContractPostService() && !payload.post_service_change_reason) {
      payload.post_service_change_reason = (changeReason || "").trim();
    }
    return payload;
  };
  
  // Check if contract has logistics assignment
  const checkHasLogistics = () => contract && (contract.vehicle_id || contract.driver_id);

  const trafficLightColors = {
    rojo: { bg: "bg-red-100", text: "text-red-800", border: "border-red-200", dot: "bg-red-500" },
    amarillo: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-200", dot: "bg-yellow-500" },
    verde: { bg: "bg-green-100", text: "text-green-800", border: "border-green-200", dot: "bg-green-500" }
  };

  const statusLabels = {
    solicitud: "Solicitud",
    cotizada: "Cotizada",
    contrato: "Contrato",
    asignada: "Asignada",
    en_curso: "En Curso",
    completado: "Completado",
    cancelado: "Cancelado",
    no_show: "No Show"
  };

  const serviceTypes = {
    transfer: "Transfer",
    aeropuerto: "Aeropuerto",
    hotel: "Hotel",
    disposicion: "Disposición"
  };

  useEffect(() => {
    console.log(`[ContratoDetalle v${COMPONENT_VERSION}] Loading contract: ${id}`);
    setRenderError(null); // Clear any previous render errors
    fetchData();
  }, [id]);

  // P2: Open client data edit dialog
  const openClientEditDialog = async () => {
    if (!contract) return;
    setClientEditData({
      client_name: contract.client_name || "",
      client_phone: contract.client_phone || "",
      client_email: contract.client_email || "",
      client_rfc: contract.client_rfc || "",
      client_legal_name: contract.client_legal_name || "",
      client_address: contract.client_address || "",
    });
    setClientEditReason("");
    setClientEditDialogOpen(true);
    // Fetch edit history
    try {
      const res = await contractsAPI.getClientEditHistory(id);
      setClientEditHistory(res.data || []);
    } catch { setClientEditHistory([]); }
  };

  // P2: Save client data edit
  const handleSaveClientEdit = async () => {
    if (!clientEditReason.trim()) {
      toast.error("El motivo de edición es obligatorio");
      return;
    }
    setClientEditLoading(true);
    try {
      const res = await contractsAPI.editClientData(id, {
        ...clientEditData,
        edit_reason: clientEditReason.trim(),
      });
      toast.success(`Datos actualizados: ${res.data.changes?.length || 0} campo(s) modificado(s)`);
      setClientEditDialogOpen(false);
      fetchData(); // Reload contract
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar datos del cliente");
    } finally {
      setClientEditLoading(false);
    }
  };

  const fetchData = async () => {
    console.log(`[ContratoDetalle v${COMPONENT_VERSION}] fetchData started`);
    setLoadError(null);
    // Reset section errors
    setPaymentsError(null);
    setAttachmentsError(null);
    setAuditError(null);
    setChangeLogsError(null);
    
    // STEP 1: Load the main contract first (CRITICAL - if this fails, show error)
    let contractData = null;
    try {
      console.log(`[ContratoDetalle] Fetching main contract...`);
      const contractRes = await contractsAPI.getOne(id);
      if (!contractRes.data) {
        setLoadError("El contrato no contiene datos válidos.");
        setLoading(false);
        return;
      }
      contractData = contractRes.data;
      setContract(contractData);
      console.log(`[ContratoDetalle] Main contract loaded: ${contractData.client_name}`);
    } catch (contractError) {
      console.error("[ContratoDetalle] Error loading contract:", contractError);
      if (contractError.response?.status === 404) {
        setLoadError("Contrato no encontrado (404)");
      } else if (contractError.response?.status === 403) {
        setLoadError("Sin permiso para ver este contrato (403)");
      } else if (contractError.code === "ECONNABORTED" || contractError.message?.includes("timeout")) {
        setLoadError("El servidor tardó demasiado en responder. Verifica tu conexión e intenta de nuevo.");
      } else {
        setLoadError(`Error de red: ${contractError.message || "Intenta de nuevo"}`);
      }
      setLoading(false);
      return;
    }
    
    // Contract loaded successfully - IMMEDIATELY stop loading spinner
    // User can see the contract while secondary data loads
    setLoading(false);
    
    // v4.59.0: leer asignaciones v2 (fire-and-forget) para banner en Resumen
    contractsAPI.getAssignments(id)
      .then(res => {
        const assigns = Array.isArray(res.data) ? res.data : (res.data?.assignments || []);
        const active = assigns.filter(a => a.status === "activa");
        setHasV2Assignments(active.length > 0);
        setActiveV2LineIds(active.map(a => a.vehicle_line_id).filter(Boolean));
      })
      .catch(() => { setHasV2Assignments(false); setActiveV2LineIds([]); });
    
    // STEP 2: Load secondary data in PARALLEL using Promise.allSettled
    // IMPORTANT: Each section loads independently - one failure does NOT affect others
    const currency = contractData.currency || "MXN";
    
    console.log(`[ContratoDetalle] Loading secondary data in parallel...`);
    
    const results = await Promise.allSettled([
      // 0: Payments
      paymentsAPI.getAll(id),
      // 1: Attachments
      attachmentsAPI.getAll(id),
      // 2: Vehicles
      vehiclesAPI.getAll(),
      // 3: Drivers
      driversAPI.getAll(),
      // 4: Payment Summary
      paymentsAPI.getContractSummary(id),
      // 5: Bank Accounts
      bankAccountsAPI.getAll({ currency, active_only: true }),
      // 6: Card Terminals
      cardTerminalsAPI.getAll({ currency, active_only: true }),
      // 7: Change Logs (Bitácora)
      contractsAPI.getLogs(id),
      // 8: Users
      usersAPI.getAll(),
      // 9: Audit Logs (only for admin)
      isAdmin ? auditAPI.getLogs({ entity_type: "contract", entity_id: id }) : Promise.resolve({ data: [] })
    ]);
    
    // Process each result independently - extract data or set error
    const processResult = (result, index, errorSetter) => {
      if (result.status === "fulfilled") {
        return result.value?.data || [];
      } else {
        console.error(`[ContratoDetalle] Section ${index} failed:`, result.reason);
        if (errorSetter) {
          errorSetter("Error al cargar esta sección");
        }
        return [];
      }
    };
    
    // 0: Payments
    const paymentsData = processResult(results[0], 0, setPaymentsError);
    setPayments(paymentsData);
    setHasPayments(paymentsData.length > 0);
    
    // 1: Attachments - LOG DEBUG P0 FIX v4.10.0
    const attachmentsData = processResult(results[1], 1, setAttachmentsError);
    console.log("[ATTACHMENTS DEBUG] Raw result:", results[1]);
    console.log("[ATTACHMENTS DEBUG] Processed data:", attachmentsData);
    console.log("[ATTACHMENTS DEBUG] Data type:", typeof attachmentsData, Array.isArray(attachmentsData));
    setAttachments(attachmentsData);
    
    // 2: Vehicles
    const vehiclesData = processResult(results[2], 2, null);
    setVehiclesAll(vehiclesData);
    setVehicles(vehiclesData.filter(v => v.status === "activo"));
    
    // 3: Drivers
    const driversData = processResult(results[3], 3, null);
    setDriversAll(driversData);
    setDrivers(driversData.filter(d => d.status === "activo"));
    
    // 4: Payment Summary & Deposit Check
    const summaryData = results[4].status === "fulfilled" ? results[4].value?.data : null;
    setPaymentSummary(summaryData);
    if (summaryData && !summaryData.deposit_met) {
      setDepositInsufficient(true);
      const shortfall = summaryData.deposit_required - summaryData.total_paid;
      setDepositShortfall(shortfall > 0 ? shortfall : 0);
    } else {
      setDepositInsufficient(false);
      setDepositShortfall(0);
    }
    
    // 5: Bank Accounts
    setBankAccounts(processResult(results[5], 5, null));
    
    // 6: Card Terminals
    setCardTerminals(processResult(results[6], 6, null));
    
    // 7: Change Logs (Bitácora)
    setChangeLogs(processResult(results[7], 7, setChangeLogsError));
    
    // 8: Users (for traceability)
    const usersData = processResult(results[8], 8, null);
    setAllUsers(usersData);
    if (contractData.created_by_user_id) {
      const creator = usersData.find(u => u.id === contractData.created_by_user_id);
      const owner = usersData.find(u => u.id === contractData.sales_owner_id);
      setCreatedByUser(creator || null);
      setSalesOwnerUser(owner || null);
    }
    
    // 9: Audit Logs
    if (isAdmin) {
      setAuditLogs(processResult(results[9], 9, setAuditError));
    }
    
    // Set initial assignment data
    setAssignmentData({
      vehicle_id: contractData.vehicle_id || "",
      driver_id: contractData.driver_id || "",
      traffic_light: contractData.traffic_light || "rojo"
    });
    setNewTrafficLight(contractData.traffic_light || "rojo");
    
    // Load vehicle types for editing
    try {
      const vtRes = await vehicleTypesAPI.getAll();
      setVehicleTypes(vtRes.data || []);
    } catch (err) {
      console.warn("Could not load vehicle types:", err);
      // Set default types as fallback
      setVehicleTypes([
        { code: "sedan", name: "Sedán Ejecutivo", capacity: 4 },
        { code: "suv", name: "SUV / Camioneta", capacity: 6 },
        { code: "van", name: "Van", capacity: 12 },
        { code: "sprinter", name: "Sprinter", capacity: 14 },
        { code: "suburban", name: "Suburban", capacity: 7 },
        { code: "bus", name: "Autobús", capacity: 45 }
      ]);
    }
    
    console.log(`[ContratoDetalle v${COMPONENT_VERSION}] All data loaded successfully`);
  };

  // Handle contract reassignment (Admin only)
  const handleReassign = async (e) => {
    e.preventDefault();
    if (!reassignData.new_sales_owner_id || !reassignData.reason.trim()) {
      toast.error("Seleccione un vendedor y proporcione una razón");
      return;
    }
    
    setReassignLoading(true);
    try {
      const response = await contractsAPI.reassign(id, reassignData);
      toast.success(response.data.message || "Contrato reasignado exitosamente");
      setReassignDialogOpen(false);
      setReassignData({ new_sales_owner_id: "", reason: "" });
      fetchData(); // Reload contract data
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al reasignar contrato"));
    } finally {
      setReassignLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    // Bloqueo anti doble-submit (v4.49.0)
    if (paymentSubmitting) return;
    
    // Validate method-specific requirements
    if (paymentData.method === "TRANSFERENCIA" && !paymentData.bank_account_id) {
      toast.error("Seleccione la cuenta bancaria destino");
      return;
    }
    if (paymentData.method === "TERMINAL" && !paymentData.terminal_id) {
      toast.error("Seleccione la terminal utilizada");
      return;
    }
    
    setPaymentSubmitting(true);
    try {
      await paymentsAPI.create({
        contract_id: id,
        amount: paymentData.amount,
        method: paymentData.method,
        reference: paymentData.reference,
        notes: paymentData.notes,
        bank_account_id: paymentData.method === "TRANSFERENCIA" ? paymentData.bank_account_id : null,
        terminal_id: paymentData.method === "TERMINAL" ? paymentData.terminal_id : null
      });
      toast.success("Pago registrado");
      setPaymentDialogOpen(false);
      setPaymentData({ amount: 0, method: "EFECTIVO", reference: "", notes: "", bank_account_id: "", terminal_id: "" });
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al registrar pago"));
    } finally {
      setPaymentSubmitting(false);
    }
  };

  // Eliminación de pago manual (admin only) — v4.49.0
  const openDeletePaymentDialog = (payment) => {
    setPaymentToDelete(payment);
    setDeletePaymentReason("");
    setDeletePaymentDialogOpen(true);
  };

  const handleDeletePayment = async () => {
    if (deletePaymentSubmitting) return;
    if (!paymentToDelete) return;
    const reason = (deletePaymentReason || "").trim();
    if (reason.length < 10) {
      toast.error("El motivo de eliminación es obligatorio (mínimo 10 caracteres)");
      return;
    }
    setDeletePaymentSubmitting(true);
    try {
      const res = await paymentsAPI.delete(id, paymentToDelete.id, { reason });
      const data = res?.data || {};
      toast.success(
        data.threshold_lost
          ? "Pago eliminado. ⚠️ La comisión cayó bajo el umbral 20% y fue revertida."
          : "Pago eliminado correctamente"
      );
      setDeletePaymentDialogOpen(false);
      setPaymentToDelete(null);
      setDeletePaymentReason("");
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al eliminar pago"));
    } finally {
      setDeletePaymentSubmitting(false);
    }
  };

  // Stripe Payment handlers for 3 payment types
  const [stripeDialogOpen, setStripeDialogOpen] = useState(false);
  const [stripePaymentType, setStripePaymentType] = useState(null);
  const [customStripeAmount, setCustomStripeAmount] = useState(0);
  const [stripeLoading, setStripeLoading] = useState(false);

  // Flexible payment link (Fase Ligas) — new dedicated dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [paymentLinksLoading, setPaymentLinksLoading] = useState(false);

  const fetchPaymentLinks = async () => {
    if (!id) return;
    setPaymentLinksLoading(true);
    try {
      const res = await paymentsAPI.listPaymentLinks(id);
      setPaymentLinks(res.data || []);
    } catch (e) {
      // Silent: vendedor sin acceso, logística, etc.
      setPaymentLinks([]);
    } finally {
      setPaymentLinksLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleStripePayment = async (paymentType, customAmount = null) => {
    setStripeLoading(true);
    try {
      const data = {
        contract_id: id,
        payment_type: paymentType,
        custom_amount: paymentType === "custom" ? customAmount : null
      };
      const response = await paymentsAPI.createStripeCheckout(data);
      
      // Open Stripe checkout in same tab
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al crear enlace de pago"));
    } finally {
      setStripeLoading(false);
      setStripeDialogOpen(false);
    }
  };

  const openStripeDialog = (type) => {
    setStripePaymentType(type);
    if (type === "custom") {
      setCustomStripeAmount(paymentSummary?.balance || 0);
    }
    setStripeDialogOpen(true);
  };

  // Mirrors backend is_post_service: end_datetime if exists, else pickup_datetime, vs now
  // v4.59.5: usa isPostServiceByDate (naive => UTC) para alinear con el backend.
  const isContractPostService = () => {
    if (!contract) return false;
    const ref = contract.end_datetime || contract.pickup_datetime;
    return isPostServiceByDate(ref);
  };

  const handleAssignment = async (e) => {
    e.preventDefault();
    const postService = isContractPostService();
    if (postService && assignmentReason.trim().length < 10) {
      toast.error("Servicio finalizado: motivo de modificación es obligatorio (mín. 10 caracteres)");
      return;
    }
    try {
      const payload = { ...assignmentData };
      if (postService) payload.post_service_change_reason = assignmentReason.trim();
      await contractsAPI.updateAssignment(id, payload);
      toast.success("Asignación actualizada");
      setAssignmentDialogOpen(false);
      setAssignmentReason("");
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al actualizar asignación"));
    }
  };

  const handleStatusChange = async (newStatus) => {
    // For "completado", show confirmation modal instead of direct change
    if (newStatus === "completado") {
      setCompletionNote("");
      setCompleteDialogOpen(true);
      return;
    }
    try {
      await contractsAPI.updateStatus(id, newStatus);
      toast.success("Estado actualizado");
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al actualizar estado"));
    }
  };

  const handleConfirmComplete = async () => {
    try {
      await contractsAPI.updateStatus(id, "completado", completionNote || undefined);
      toast.success("Contrato marcado como completado");
      setCompleteDialogOpen(false);
      setCompletionNote("");
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al completar contrato"));
    }
  };

  // Helpers for completion warnings
  const getCompletionWarnings = () => {
    if (!contract) return [];
    const warnings = [];
    const hasVehicle = contract.vehicle_id || (contract.vehicle_lines || []).some(vl => vl.assigned_vehicle_id);
    const hasDriver = contract.driver_id || (contract.vehicle_lines || []).some(vl => vl.assigned_driver_id);
    if (!hasVehicle) warnings.push("Este contrato NO tiene vehiculo asignado");
    if (!hasDriver) warnings.push("Este contrato NO tiene chofer asignado");
    return warnings;
  };

  const handleTrafficLightChange = async (e) => {
    e.preventDefault();
    const postService = isContractPostService();
    if (postService && trafficReason.trim().length < 10) {
      toast.error("Servicio finalizado: motivo de cambio es obligatorio (mín. 10 caracteres)");
      return;
    }
    try {
      const payload = { traffic_light: newTrafficLight };
      if (postService) payload.post_service_change_reason = trafficReason.trim();
      await contractsAPI.updateAssignment(id, payload);
      toast.success("Semáforo actualizado");
      setTrafficDialogOpen(false);
      setTrafficReason("");
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al actualizar semáforo"));
    }
  };

  // Financial fields that require change_reason
  const financialFields = ["base_price", "extras", "vat_rate", "currency", "exchange_rate", "vehicle_lines", "requires_invoice"];
  
  // Operational fields that require confirmation if logistics assigned
  const operationalFields = ["pickup_datetime", "end_datetime", "service_type", "origin", "destination", "passengers"];
  
  // Check if vehicle_lines changed (compare by stringified values)
  const checkIfVehicleLinesChanged = () => {
    if (!contract) return false;
    const oldLines = contract.vehicle_lines || [];
    const newLines = editData.vehicle_lines || [];
    return JSON.stringify(oldLines) !== JSON.stringify(newLines);
  };
  
  const checkIfFinancialFieldsChanged = () => {
    if (!contract) return false;
    // Check vehicle_lines separately due to array comparison
    if (checkIfVehicleLinesChanged()) return true;
    // Check other financial fields
    return financialFields.filter(f => f !== "vehicle_lines").some(field => {
      const oldVal = contract[field];
      const newVal = editData[field];
      if (oldVal === undefined && newVal === undefined) return false;
      if (oldVal === null && newVal === "") return false;
      return String(oldVal) !== String(newVal);
    });
  };
  
  const checkIfOperationalFieldsChanged = () => {
    if (!contract) return false;
    return operationalFields.some(field => {
      const oldVal = contract[field];
      const newVal = editData[field];
      if (oldVal === undefined && newVal === undefined) return false;
      if (oldVal === null && newVal === "") return false;
      return String(oldVal) !== String(newVal);
    });
  };

  // Vehicle line management functions for edit mode
  const handleAddVehicleLineEdit = () => {
    if (newVehicleLine.quantity > 0 && newVehicleLine.type) {
      const newLines = [];
      const basePrice = newVehicleLine.base_price || 0;
      
      // Expand quantity to individual entries
      for (let i = 0; i < newVehicleLine.quantity; i++) {
        newLines.push({
          id: `${Date.now()}-${i}`,
          type: newVehicleLine.type,
          capacity: newVehicleLine.capacity,
          base_price: basePrice,
          notes: ""
        });
      }
      
      setEditData(prev => ({
        ...prev,
        vehicle_lines: [...(prev.vehicle_lines || []), ...newLines]
      }));
      
      const defaultType = vehicleTypes[0] || { code: "sedan", capacity: 4 };
      setNewVehicleLine({ type: defaultType.code, quantity: 1, capacity: defaultType.capacity || 4, base_price: 0 });
    }
  };

  const handleRemoveVehicleLineEdit = (lineId) => {
    setEditData(prev => ({
      ...prev,
      vehicle_lines: (prev.vehicle_lines || []).filter(v => v.id !== lineId)
    }));
  };

  const handleUpdateVehicleLinePriceEdit = (lineId, newPrice) => {
    setEditData(prev => ({
      ...prev,
      vehicle_lines: (prev.vehicle_lines || []).map(v => 
        v.id === lineId ? { ...v, base_price: parseFloat(newPrice) || 0 } : v
      )
    }));
  };

  // v4.59.3: Edición de intención operativa (line_*) por línea en modo edición de contrato
  const LINE_INTENT_KEYS = ["line_start_datetime", "line_end_datetime", "line_origin", "line_destination", "line_notes"];

  const updateLineIntentFieldEdit = (lineId, field, value) => {
    setEditData(prev => ({
      ...prev,
      vehicle_lines: (prev.vehicle_lines || []).map(v =>
        v.id === lineId ? { ...v, [field]: value } : v
      )
    }));
  };

  const inheritGlobalForLineEdit = (lineId) => {
    setEditData(prev => ({
      ...prev,
      vehicle_lines: (prev.vehicle_lines || []).map(v => {
        if (v.id !== lineId) return v;
        const cleared = { ...v };
        LINE_INTENT_KEYS.forEach(k => { cleared[k] = null; });
        return cleared;
      })
    }));
  };

  const toggleEditLineExpand = (lineId) => {
    setExpandedEditLineIds(prev =>
      prev.includes(lineId) ? prev.filter(id => id !== lineId) : [...prev, lineId]
    );
  };

  // Calculate totals from vehicle lines (edit mode)
  const calculateEditTotals = () => {
    const lines = editData.vehicle_lines || [];
    const vehicleLinesTotal = lines.reduce((sum, v) => sum + (v.base_price || 0), 0);
    const extras = editData.extras || [];
    const extrasTotal = Array.isArray(extras) ? extras.reduce((sum, e) => sum + (e.amount || 0), 0) : 0;
    const subtotal = vehicleLinesTotal + extrasTotal;
    const vatRate = editData.vat_rate || 0.16;
    const vatAmount = editData.requires_invoice ? subtotal * vatRate : 0;
    const total = subtotal + vatAmount;
    return { vehicleLinesTotal, extrasTotal, subtotal, vatAmount, total };
  };

  const handleSaveChanges = async () => {
    // Validate operational fields - passengers now optional
    if (editData.passengers !== undefined && editData.passengers !== "" && editData.passengers <= 0) {
      toast.error("El número de pasajeros debe ser mayor a 0 si se especifica");
      return;
    }
    if (editData.origin !== undefined && !editData.origin.trim()) {
      toast.error("El origen no puede estar vacío");
      return;
    }
    if (editData.destination !== undefined && !editData.destination.trim()) {
      toast.error("El destino no puede estar vacío");
      return;
    }
    // P0: Validate dates - end must be after start
    if (editData.pickup_datetime && editData.end_datetime) {
      if (new Date(editData.end_datetime) <= new Date(editData.pickup_datetime)) {
        toast.error("La fecha y hora de finalización debe ser posterior a la fecha y hora de inicio");
        return;
      }
    }
    // Validate vehicle_lines - at least one if contract is new format
    if (editData.vehicle_lines && editData.vehicle_lines.length > 0) {
      const hasInvalidPrice = editData.vehicle_lines.some(v => !v.base_price || v.base_price <= 0);
      if (hasInvalidPrice) {
        toast.error("Todas las unidades deben tener un precio mayor a 0");
        return;
      }
    }
    
    // Check if financial fields changed
    const financialChanged = checkIfFinancialFieldsChanged();
    const operationalChanged = checkIfOperationalFieldsChanged();
    
    // If has logistics assignment and operational fields changed, show confirmation
    // Owner-limited path uses change_reason as the universal reason (backend auto-derives operational_change_reason)
    if (!isOwnerLimited && hasLogisticsAssignment && operationalChanged && !operationalChangeReason) {
      setPendingChanges(editData);
      setLogisticsConfirmOpen(true);
      return;
    }

    // For owner-limited edits: always require a change_reason via dialog
    if (isOwnerLimited && (!changeReason || changeReason.trim().length < 10)) {
      setPendingChanges(editData);
      setChangeReasonDialogOpen(true);
      return;
    }

    if (financialChanged && !changeReason) {
      // Need to ask for reason
      setPendingChanges(editData);
      setChangeReasonDialogOpen(true);
      return;
    }
    
    setEditLoading(true);
    try {
      // Prepare update payload with proper type conversions
      const updatePayload = {
        ...editData,
        // Ensure numeric fields are proper numbers
        passengers: editData.passengers !== undefined && editData.passengers !== "" 
          ? parseInt(editData.passengers, 10) || null 
          : undefined,
        luggage: editData.luggage !== undefined && editData.luggage !== "" 
          ? parseInt(editData.luggage, 10) || null 
          : undefined,
        base_price: editData.base_price !== undefined 
          ? parseFloat(editData.base_price) || 0 
          : undefined,
        total_price: editData.total_price !== undefined 
          ? parseFloat(editData.total_price) || 0 
          : undefined,
        subtotal_amount: editData.subtotal_amount !== undefined 
          ? parseFloat(editData.subtotal_amount) || 0 
          : undefined,
        vat_amount: editData.vat_amount !== undefined 
          ? parseFloat(editData.vat_amount) || 0 
          : undefined,
        exchange_rate: editData.exchange_rate !== undefined 
          ? parseFloat(editData.exchange_rate) || null 
          : undefined,
        // FIX v4.11.5: Normalize extras to array format expected by backend
        extras: normalizeExtrasForAPI(editData.extras, contract?.extras),
        // Ensure vehicle_lines have proper numeric base_price
        vehicle_lines: editData.vehicle_lines?.map(v => ({
          ...v,
          base_price: parseFloat(v.base_price) || 0,
          quantity: parseInt(v.quantity, 10) || 1
        })),
        change_reason: financialChanged ? changeReason : undefined,
        operational_change_reason: (hasLogisticsAssignment && operationalChanged) ? operationalChangeReason : undefined,
        admin_override: isAdmin ? true : undefined
      };
      
      // Remove undefined fields
      Object.keys(updatePayload).forEach(key => {
        if (updatePayload[key] === undefined) delete updatePayload[key];
      });

      applyOwnerLimitedFilter(updatePayload);

      // VL/Logistica: only send operational fields (backend blocks price fields)
      if (isLogistica && !isAdmin) {
        const allowedFields = new Set([
          'pickup_datetime', 'end_datetime', 'service_type',
          'origin', 'destination', 'passengers', 'luggage',
          'flight_number', 'full_itinerary', 'special_requirements',
          'notes', 'internal_notes',
          'operational_change_reason'
        ]);
        Object.keys(updatePayload).forEach(key => {
          if (!allowedFields.has(key)) delete updatePayload[key];
        });
      }
      
      await contractsAPI.update(id, updatePayload);
      toast.success("Contrato actualizado");
      setEditMode(false);
      setEditData({});
      setChangeReason("");
      setOperationalChangeReason("");
      setPendingChanges(null);
      fetchData();
    } catch (error) {
      // Handle error - extract message from different error formats
      let errorMsg = "Error al actualizar contrato";
      const detail = error.response?.data?.detail;
      
      if (detail) {
        if (Array.isArray(detail)) {
          // FastAPI validation errors come as array
          errorMsg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(", ");
        } else if (typeof detail === 'object') {
          // Single validation error object
          errorMsg = detail.msg || detail.message || JSON.stringify(detail);
        } else {
          errorMsg = String(detail);
        }
      }
      
      toast.error(errorMsg);
      
      // If error is about operational_change_reason, show logistics dialog
      if (errorMsg.includes("operational_change_reason") || errorMsg.includes("asignación logística")) {
        setPendingChanges(editData);
        setLogisticsConfirmOpen(true);
      }
      // If error is about change_reason, show dialog
      else if (errorMsg.includes("change_reason") || errorMsg.includes("razón")) {
        setPendingChanges(editData);
        setChangeReasonDialogOpen(true);
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleConfirmLogisticsChange = async () => {
    if (!operationalChangeReason.trim() || operationalChangeReason.trim().length < 10) {
      toast.error("Debe proporcionar una razón de al menos 10 caracteres");
      return;
    }
    setLogisticsConfirmOpen(false);
    
    // Check if also needs financial reason
    const financialChanged = checkIfFinancialFieldsChanged();
    if (financialChanged && !changeReason) {
      setChangeReasonDialogOpen(true);
      return;
    }
    
    // Proceed to save
    setEditLoading(true);
    try {
      // Apply same type conversions as handleSaveChanges
      const normalizedChanges = {
        ...pendingChanges,
        passengers: pendingChanges.passengers !== undefined && pendingChanges.passengers !== "" 
          ? parseInt(pendingChanges.passengers, 10) || null 
          : undefined,
        luggage: pendingChanges.luggage !== undefined && pendingChanges.luggage !== "" 
          ? parseInt(pendingChanges.luggage, 10) || null 
          : undefined,
        base_price: pendingChanges.base_price !== undefined 
          ? parseFloat(pendingChanges.base_price) || 0 
          : undefined,
        total_price: pendingChanges.total_price !== undefined 
          ? parseFloat(pendingChanges.total_price) || 0 
          : undefined,
        subtotal_amount: pendingChanges.subtotal_amount !== undefined 
          ? parseFloat(pendingChanges.subtotal_amount) || 0 
          : undefined,
        vat_amount: pendingChanges.vat_amount !== undefined 
          ? parseFloat(pendingChanges.vat_amount) || 0 
          : undefined,
        exchange_rate: pendingChanges.exchange_rate !== undefined 
          ? parseFloat(pendingChanges.exchange_rate) || null 
          : undefined,
        // FIX v4.11.5: Normalize extras to array format expected by backend
        extras: normalizeExtrasForAPI(pendingChanges.extras, contract?.extras),
        vehicle_lines: pendingChanges.vehicle_lines?.map(v => ({
          ...v,
          base_price: parseFloat(v.base_price) || 0,
          quantity: parseInt(v.quantity, 10) || 1,
          capacity: v.capacity !== undefined ? parseInt(v.capacity, 10) || 0 : undefined
        }))
      };
      
      const updatePayload = {
        ...normalizedChanges,
        operational_change_reason: operationalChangeReason,
        change_reason: financialChanged ? changeReason : undefined,
        admin_override: isAdmin ? true : undefined
      };
      
      Object.keys(updatePayload).forEach(key => {
        if (updatePayload[key] === undefined) delete updatePayload[key];
      });

      applyOwnerLimitedFilter(updatePayload);

      // VL/Logistica: only send operational fields (backend blocks price fields)
      if (isLogistica && !isAdmin) {
        const allowedFields = new Set([
          'pickup_datetime', 'end_datetime', 'service_type',
          'origin', 'destination', 'passengers', 'luggage',
          'flight_number', 'full_itinerary', 'special_requirements',
          'notes', 'internal_notes',
          'operational_change_reason'
        ]);
        Object.keys(updatePayload).forEach(key => {
          if (!allowedFields.has(key)) delete updatePayload[key];
        });
      }
      
      await contractsAPI.update(id, updatePayload);
      toast.success("Contrato actualizado");
      setEditMode(false);
      setEditData({});
      setChangeReason("");
      setOperationalChangeReason("");
      setPendingChanges(null);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al actualizar contrato"));
    } finally {
      setEditLoading(false);
    }
  };

  const handleConfirmChangeReason = async () => {
    if (!changeReason.trim()) {
      toast.error("Debe proporcionar una razón para el cambio");
      return;
    }
    setChangeReasonDialogOpen(false);
    
    // Now call save with the reason
    setEditLoading(true);
    try {
      // Apply same type conversions as handleSaveChanges
      const normalizedChanges = {
        ...pendingChanges,
        // Ensure numeric fields are proper numbers
        passengers: pendingChanges.passengers !== undefined && pendingChanges.passengers !== "" 
          ? parseInt(pendingChanges.passengers, 10) || null 
          : undefined,
        luggage: pendingChanges.luggage !== undefined && pendingChanges.luggage !== "" 
          ? parseInt(pendingChanges.luggage, 10) || null 
          : undefined,
        base_price: pendingChanges.base_price !== undefined 
          ? parseFloat(pendingChanges.base_price) || 0 
          : undefined,
        total_price: pendingChanges.total_price !== undefined 
          ? parseFloat(pendingChanges.total_price) || 0 
          : undefined,
        subtotal_amount: pendingChanges.subtotal_amount !== undefined 
          ? parseFloat(pendingChanges.subtotal_amount) || 0 
          : undefined,
        vat_amount: pendingChanges.vat_amount !== undefined 
          ? parseFloat(pendingChanges.vat_amount) || 0 
          : undefined,
        exchange_rate: pendingChanges.exchange_rate !== undefined 
          ? parseFloat(pendingChanges.exchange_rate) || null 
          : undefined,
        // FIX v4.11.5: Normalize extras to array format expected by backend
        extras: normalizeExtrasForAPI(pendingChanges.extras, contract?.extras),
        // Ensure vehicle_lines have proper numeric values
        vehicle_lines: pendingChanges.vehicle_lines?.map(v => ({
          ...v,
          base_price: parseFloat(v.base_price) || 0,
          quantity: parseInt(v.quantity, 10) || 1,
          capacity: v.capacity !== undefined ? parseInt(v.capacity, 10) || 0 : undefined
        }))
      };
      
      const updatePayload = {
        ...normalizedChanges,
        change_reason: changeReason,
        admin_override: isAdmin ? true : undefined
      };
      
      Object.keys(updatePayload).forEach(key => {
        if (updatePayload[key] === undefined) delete updatePayload[key];
      });

      applyOwnerLimitedFilter(updatePayload);

      // VL/Logistica: only send operational fields (backend blocks price fields)
      if (isLogistica && !isAdmin) {
        const allowedFields = new Set([
          'pickup_datetime', 'end_datetime', 'service_type',
          'origin', 'destination', 'passengers', 'luggage',
          'flight_number', 'full_itinerary', 'special_requirements',
          'notes', 'internal_notes',
          'operational_change_reason'
        ]);
        Object.keys(updatePayload).forEach(key => {
          if (!allowedFields.has(key)) delete updatePayload[key];
        });
      }
      
      await contractsAPI.update(id, updatePayload);
      toast.success("Contrato actualizado");
      setEditMode(false);
      setEditData({});
      setChangeReason("");
      setPendingChanges(null);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al actualizar contrato"));
    } finally {
      setEditLoading(false);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      await attachmentsAPI.upload(id, type, file);
      toast.success("Archivo subido");
      fetchData();
    } catch (error) {
      toast.error("Error al subir archivo");
    }
  };

  const handleDownload = async (attachmentId, filename) => {
    try {
      const response = await attachmentsAPI.download(attachmentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Error al descargar archivo");
    }
  };

  const handleDeleteAttachment = async (attachmentId, filename) => {
    if (!window.confirm(`¿Está seguro de eliminar el documento "${filename}"?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      await attachmentsAPI.delete(attachmentId);
      toast.success(`Documento "${filename}" eliminado`);
      fetchData(); // Refresh attachments list
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error al eliminar documento"));
    }
  };

  // Download contract PDF (juridical/formal document)
  const handleDownloadContractPDF = async () => {
    setPdfLoading(true);
    try {
      const response = await contractsAPI.getPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(url, '_blank');
      
      toast.success("PDF del contrato generado");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error(extractErrorMessage(error, "Error al generar PDF"));
    } finally {
      setPdfLoading(false);
    }
  };

  // Download Service Order PDF (for operators)
  const [serviceOrderLoading, setServiceOrderLoading] = useState(false);
  
  const handleDownloadServiceOrderPDF = async () => {
    setServiceOrderLoading(true);
    try {
      const response = await contractsAPI.getServiceOrderPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(url, '_blank');
      
      toast.success("Orden de servicio generada");
    } catch (error) {
      console.error("Error downloading service order:", error);
      toast.error(extractErrorMessage(error, "Error al generar orden de servicio"));
    } finally {
      setServiceOrderLoading(false);
    }
  };
  
  // NEW: Load service orders by unit
  const loadServiceOrdersByUnit = async () => {
    setServiceOrdersLoading(true);
    try {
      const response = await contractsAPI.getServiceOrders(id);
      setServiceOrders(response.data || []);
      setServiceOrdersDialogOpen(true);
    } catch (error) {
      console.error("Error loading service orders:", error);
      toast.error("Error al cargar órdenes de servicio");
    } finally {
      setServiceOrdersLoading(false);
    }
  };
  
  // NEW: Download individual service order PDF
  const handleDownloadUnitServiceOrderPDF = async (unitId, orderLabel) => {
    try {
      const response = await contractsAPI.getUnitServiceOrderPDF(id, unitId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success(`Orden de servicio generada: ${orderLabel}`);
    } catch (error) {
      console.error("Error downloading unit service order:", error);
      toast.error("Error al generar orden de servicio");
    }
  };
  
  // Get vehicle type label - uses catalog when available, flags ambiguous legacy codes
  // DOES NOT infer/guess for ambiguous codes - flags them for review
  const getVehicleTypeLabel = (typeCode) => {
    try {
      if (!typeCode) return "Sin tipo";
      
      // Ensure vehicleTypes is an array
      if (!Array.isArray(vehicleTypes)) {
        console.warn("[getVehicleTypeLabel] vehicleTypes is not an array:", vehicleTypes);
        return typeCode;
      }
      
      // 1. Try exact match
      const exactMatch = vehicleTypes.find(vt => vt.code === typeCode);
      if (exactMatch) {
        return exactMatch.name;
      }
      
      // 2. Check for ambiguous legacy code
      const possibleMatches = vehicleTypes.filter(vt => 
        vt.code.startsWith(typeCode + "_") || 
        vt.code.startsWith(typeCode.toLowerCase() + "_")
      );
      
      if (possibleMatches.length > 0) {
        const baseName = typeCode.charAt(0).toUpperCase() + typeCode.slice(1).toLowerCase();
        return `${baseName} (revisar capacidad)`;
      }
      
      // 3. Simple fallback
      const fallbackLabels = {
        sedan: "Sedán",
        suv: "SUV",
        van: "Van",
        sprinter: "Sprinter",
        suburban: "Suburban",
        autobus: "Autobús (revisar capacidad)",
        bus: "Autobús (revisar capacidad)",
        limusina: "Limusina"
      };
      
      return fallbackLabels[typeCode.toLowerCase()] || typeCode;
    } catch (error) {
      console.error("[getVehicleTypeLabel] Error:", error, "typeCode:", typeCode);
      return typeCode || "Error";
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state - show user-friendly message with version for debugging
  if (loadError) {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <div className="max-w-md mx-auto mt-12">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-red-800">Error al cargar contrato</h2>
                  <p className="text-red-700">{loadError}</p>
                  <div className="flex gap-3 justify-center pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => navigate("/contratos")}
                      className="gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Volver a Contratos
                    </Button>
                    <Button 
                      onClick={() => {
                        setLoading(true);
                        setLoadError(null);
                        fetchData();
                      }}
                      className="gap-2"
                    >
                      Reintentar
                    </Button>
                  </div>
                  {/* Version indicator for deployment verification */}
                  <p className="text-xs text-slate-400 mt-4">v{COMPONENT_VERSION}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  if (!contract) return null;

  // Catch any render errors early
  if (renderError) {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <Card className="border-red-300 bg-red-50">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-red-800 mb-4">Error al mostrar el contrato</h2>
              <p className="text-red-700 mb-4">{renderError}</p>
              <p className="text-sm text-red-600 mb-4">
                ID: {id}<br />
                Versión: {COMPONENT_VERSION}
              </p>
              <div className="flex gap-2">
                <Button onClick={() => window.location.reload()}>Recargar página</Button>
                <Button variant="outline" onClick={() => navigate("/contratos")}>Volver a contratos</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const totalPaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const balance = (contract.total_price || 0) - totalPaid;

  // v4.53.0: Indicador "Contrato listo para operación" — solo cómputo cliente, sin escrituras
  const computeOperationalReadiness = (c) => {
    if (!c) return { ready: false, pending: [] };
    const pending = [];
    if (!c.pickup_datetime) pending.push("Falta fecha/hora de inicio");
    if (!c.end_datetime) pending.push("Falta fecha/hora de finalización");
    if (!(c.origin || "").trim()) pending.push("Falta origen");
    if (!(c.destination || "").trim()) pending.push("Falta destino");
    const lines = Array.isArray(c.vehicle_lines) ? c.vehicle_lines : [];
    if (lines.length > 0) {
      const missingV = lines.filter((vl) => !vl?.assigned_vehicle_id).length;
      const missingD = lines.filter((vl) => !vl?.assigned_driver_id).length;
      if (missingV > 0) pending.push(`Falta unidad asignada (${missingV}/${lines.length} líneas)`);
      if (missingD > 0) pending.push(`Falta chofer asignado (${missingD}/${lines.length} líneas)`);
    } else {
      if (!c.vehicle_id) pending.push("Falta unidad asignada");
      if (!c.driver_id) pending.push("Falta chofer asignado");
    }
    return { ready: pending.length === 0, pending };
  };
  const readiness = computeOperationalReadiness(contract);
  const vehicle = vehiclesAll.find(v => v.id === contract.vehicle_id);
  const driver = driversAll.find(d => d.id === contract.driver_id);

  // v4.56.0: helpers para selectores de asignación (nueva o edición histórica)
  const getVehiclesForForm = (currentId = null) => {
    if (currentId && !vehicles.find(v => v.id === currentId)) {
      const historical = vehiclesAll.find(v => v.id === currentId);
      if (historical) return [...vehicles, historical];
    }
    return vehicles;
  };
  const getDriversForForm = (currentId = null) => {
    if (currentId && !drivers.find(d => d.id === currentId)) {
      const historical = driversAll.find(d => d.id === currentId);
      if (historical) return [...drivers, historical];
    }
    return drivers;
  };
  const vehicleSelectLabel = (v) => {
    const base = formatVehicleLabel(v);
    return v?.status === "inactivo" ? `${base} (inactiva)` : base;
  };
  const driverSelectLabel = (d) => {
    const base = `${d?.name || ""} - ${d?.phone || ""}`;
    return d?.status === "inactivo" ? `${base} (inactivo)` : base;
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="contract-detail-page">
        {/* Deposit Insufficient Warning Banner */}
        {depositInsufficient && depositShortfall > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3" data-testid="deposit-warning-banner">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">Anticipo Insuficiente</h3>
              <p className="text-sm text-amber-700">
                Faltan <strong>${depositShortfall.toLocaleString("es-MX")}</strong> para completar el anticipo mínimo requerido (20%).
              </p>
              <Button 
                size="sm" 
                className="mt-2 bg-amber-600 hover:bg-amber-700"
                onClick={() => handleStripePayment("deposit")}
                disabled={stripeLoading}
                data-testid="deposit-warning-pay-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Generar link de anticipo
              </Button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/contratos")} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-heading font-bold text-slate-900">
                  {contract.client_name}
                </h1>
                <Badge className={cn(
                  "text-sm",
                  trafficLightColors[contract.traffic_light]?.bg,
                  trafficLightColors[contract.traffic_light]?.text
                )}>
                  <Circle className={cn("w-2 h-2 mr-1 fill-current", trafficLightColors[contract.traffic_light]?.text)} />
                  {contract.traffic_light?.toUpperCase()}
                </Badge>
                {depositInsufficient && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="deposit-insufficient-badge">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Anticipo insuficiente
                  </Badge>
                )}
                {/* v4.53.0: Indicador Listo para Operación / Pendiente Operativo */}
                {readiness.ready ? (
                  <Badge
                    className="bg-emerald-100 text-emerald-800 border-emerald-300"
                    data-testid="operational-readiness-ready-badge"
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Listo para operar
                  </Badge>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 hover:bg-amber-200 transition-colors cursor-pointer"
                        data-testid="operational-readiness-pending-badge"
                        aria-label={`Pendiente operativo: ${readiness.pending.length} item${readiness.pending.length === 1 ? "" : "s"}`}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        Pendiente operativo ({readiness.pending.length})
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72" align="start" data-testid="operational-readiness-popover">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-semibold text-slate-800">
                            Pendientes operativos
                          </span>
                        </div>
                        <ul className="space-y-1.5">
                          {readiness.pending.map((p, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-2 text-sm text-slate-700"
                              data-testid={`operational-pending-item-${idx}`}
                            >
                              <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"></span>
                              <span>{p}</span>
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
                          Indicador informativo. No bloquea acciones.
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <p className="text-slate-500 mt-1 font-mono">{contract.folio || contract.id.slice(0, 8)}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {canEditContract && !editMode && (
              <Button 
                variant="outline" 
                onClick={() => {
                  // Prepare vehicle_lines for editing
                  // For legacy contracts without base_price per unit, calculate from total base_price
                  let vehicleLines = contract.vehicle_lines || [];
                  if (vehicleLines.length > 0) {
                    // Check if any line has a base_price
                    const hasIndividualPrices = vehicleLines.some(vl => vl.base_price && vl.base_price > 0);
                    if (!hasIndividualPrices && contract.base_price > 0) {
                      // Legacy contract: distribute base_price evenly across units
                      const pricePerUnit = Math.round((contract.base_price / vehicleLines.length) * 100) / 100;
                      vehicleLines = vehicleLines.map((vl, idx) => ({
                        ...vl,
                        id: vl.id || `legacy-${idx}-${Date.now()}`,
                        base_price: pricePerUnit
                      }));
                    } else {
                      // Ensure all lines have IDs
                      vehicleLines = vehicleLines.map((vl, idx) => ({
                        ...vl,
                        id: vl.id || `edit-${idx}-${Date.now()}`
                      }));
                    }
                  }
                  
                  setEditData({
                    // Client info
                    client_name: contract.client_name || "",
                    client_phone: contract.client_phone || "",
                    client_email: contract.client_email || "",
                    // Operational fields
                    origin: contract.origin || "",
                    destination: contract.destination || "",
                    pickup_datetime: contract.pickup_datetime || "",
                    end_datetime: contract.end_datetime || "",
                    service_type: contract.service_type || "transfer",
                    passengers: contract.passengers || "",  // Optional - can be empty
                    luggage: contract.luggage || "",  // Optional
                    flight_number: contract.flight_number || "",  // Optional
                    // NEW: Full itinerary
                    full_itinerary: contract.full_itinerary || "",
                    special_requirements: contract.special_requirements || "",
                    // Notes
                    notes: contract.notes || "",
                    internal_notes: contract.internal_notes || "",
                    // Vehicle lines - NEW: Individual pricing per unit (pre-populated for legacy)
                    vehicle_lines: vehicleLines,
                    // Financial fields
                    base_price: contract.base_price || 0,
                    extras: contract.extras || [],
                    vat_rate: contract.vat_rate || 0.16,
                    requires_invoice: contract.requires_invoice || false,
                    currency: contract.currency || "MXN",
                    exchange_rate: contract.exchange_rate || 1,
                  });
                  setHasLogisticsAssignment(!!contract.vehicle_id || !!contract.driver_id);
                  setEditMode(true);
                }} 
                data-testid="edit-contract-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            )}
            {editMode && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEditMode(false);
                    setEditData({});
                    setOperationalChangeReason("");
                  }}
                  data-testid="cancel-edit-btn"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveChanges}
                  disabled={editLoading}
                  data-testid="save-changes-btn"
                >
                  {editLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Guardar Cambios
                </Button>
              </>
            )}
            {!editMode && canManageAssignments && (
              <Button variant="outline" onClick={() => setAssignmentDialogOpen(true)} data-testid="assignment-btn">
                <Truck className="w-4 h-4 mr-2" />
                Asignar
              </Button>
            )}
            {!editMode && !canManageAssignments && (
              <Button variant="outline" onClick={() => setTrafficDialogOpen(true)} data-testid="traffic-btn">
                <Circle className="w-4 h-4 mr-2" />
                Cambiar Semáforo
              </Button>
            )}
            {!editMode && (
              <Button 
                variant="outline"
                onClick={handleDownloadContractPDF}
                disabled={pdfLoading}
                data-testid="download-pdf-btn"
              >
                {pdfLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                Contrato PDF
              </Button>
            )}
            {!editMode && (
              <Button 
                variant="outline"
                onClick={loadServiceOrdersByUnit}
                disabled={serviceOrdersLoading}
                data-testid="download-service-order-btn"
              >
                {serviceOrdersLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ClipboardList className="w-4 h-4 mr-2" />
                )}
                Órdenes de Servicio
              </Button>
            )}
            {!editMode && isAdmin && (
              <Button onClick={() => setPaymentDialogOpen(true)} data-testid="payment-btn">
                <CreditCard className="w-4 h-4 mr-2" />
                Registrar Pago
              </Button>
            )}
            {!editMode && isAdmin && (
              <Button 
                variant="outline" 
                onClick={() => setReassignDialogOpen(true)} 
                data-testid="reassign-btn"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Reasignar
              </Button>
            )}
            {!editMode && !isAdmin && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button disabled variant="outline" data-testid="payment-btn-disabled">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Registrar Pago
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Solo administración puede registrar o validar pagos
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="resumen" className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="resumen" data-testid="tab-resumen">Resumen</TabsTrigger>
            <TabsTrigger value="operacion" data-testid="tab-operacion">Operación</TabsTrigger>
            <TabsTrigger value="pagos" data-testid="tab-pagos">Pagos</TabsTrigger>
            <TabsTrigger value="bitacora" data-testid="tab-bitacora">Bitácora</TabsTrigger>
            <TabsTrigger value="adjuntos" data-testid="tab-adjuntos">Adjuntos</TabsTrigger>
            {isAdmin && <TabsTrigger value="auditoria" data-testid="tab-auditoria">Auditoría</TabsTrigger>}
          </TabsList>

          {/* Resumen Tab */}
          <TabsContent value="resumen" className="space-y-6">
            {/* Traceability Card */}
            {(createdByUser || salesOwnerUser) && (
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-6">
                    {createdByUser && (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-600">Creado por:</span>
                        <span className="text-sm font-medium">{createdByUser.name}</span>
                      </div>
                    )}
                    {salesOwnerUser && (
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-blue-500" />
                        <span className="text-sm text-slate-600">Vendedor asignado:</span>
                        <span className="text-sm font-medium text-blue-700">{salesOwnerUser.name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-600">Creado:</span>
                      <span className="text-sm font-medium">
                        {contract.created_at ? new Date(contract.created_at).toLocaleString("es-MX") : "N/A"}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Fase 1: Owner edición limitada — banner informativo */}
            {editMode && isOwnerLimited && (
              <Card className="bg-blue-50 border-blue-300" data-testid="owner-limited-banner">
                <CardContent className="p-3 text-sm text-blue-900 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Edición limitada para vendedor owner</p>
                    <p className="text-xs text-blue-800 mt-0.5">
                      Sólo puedes editar: <strong>origen, destino, fecha/hora de inicio, fecha/hora de finalización e itinerario</strong>.
                      Cualquier cambio requerirá un motivo (mín. 10 caracteres) y quedará registrado en bitácora.
                      {isContractPostService() && " El servicio ya finalizó: el motivo será también capturado como motivo post-servicio."}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Client Info */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Cliente
                    {editMode && <Badge className="bg-blue-100 text-blue-700 ml-2">Editando</Badge>}
                    {contract.contractual_data_override && (
                      <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600 ml-1">Datos editados</Badge>
                    )}
                  </CardTitle>
                  {isAdmin && !editMode && (
                    <Button variant="outline" size="sm" onClick={openClientEditDialog} data-testid="edit-client-data-btn" className="h-7 text-xs">
                      <FileEdit className="w-3 h-3 mr-1" /> Editar Datos Contractuales
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* P2: Show last edit info */}
                  {contract.contractual_data_last_edited_at && (
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg mb-3 text-xs text-amber-700 flex items-center justify-between">
                      <span>Datos contractuales editados el {new Date(contract.contractual_data_last_edited_at).toLocaleString("es-MX")}</span>
                      <Button variant="ghost" size="sm" className="h-5 text-[10px] text-amber-600" onClick={() => { openClientEditDialog(); setShowClientEditHistory(true); }}>
                        Ver historial
                      </Button>
                    </div>
                  )}
                  {/* CRM Client Selector for legacy migration */}
                  {!contract.client_id && contract.client_name && isAdmin && (
                    <div className="mb-4">
                      <ClientSelector
                        value={null}
                        onChange={(clientId, clientData) => {
                          if (clientId) {
                            // Reload contract to get updated data
                            fetchContractData();
                            toast.success("Cliente vinculado exitosamente");
                          }
                        }}
                        legacyClientName={contract.client_name}
                        legacyClientPhone={contract.client_phone}
                        legacyClientEmail={contract.client_email}
                        showLegacyFields={false}
                        sourceType="contract"
                        sourceId={contract.id}
                        isAdmin={isAdmin}
                      />
                    </div>
                  )}
                  
                  {/* Show CRM client badge if linked */}
                  {contract.client_id && contract.crm_client && (
                    <div className="p-2 bg-green-50 border border-green-200 rounded-lg mb-3">
                      <div className="flex items-center gap-2 text-green-700">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">Cliente CRM vinculado</span>
                        <Badge variant="outline" className="text-xs">
                          {contract.crm_client.client_type === "individual" ? "Persona Física" : "Persona Moral"}
                        </Badge>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm text-slate-500">Nombre</p>
                    {editMode && !isOwnerLimited ? (
                      <Input
                        value={editData.client_name || ""}
                        onChange={(e) => setEditData({...editData, client_name: e.target.value})}
                        data-testid="edit-client-name"
                        disabled={!!contract.client_id}
                      />
                    ) : (
                      <p className="font-medium">{contract.client_name}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Teléfono</p>
                    {editMode && !isOwnerLimited ? (
                      <Input
                        value={editData.client_phone || ""}
                        onChange={(e) => setEditData({...editData, client_phone: e.target.value})}
                        data-testid="edit-client-phone"
                        disabled={!!contract.client_id}
                      />
                    ) : (
                      <p className="font-medium">{contract.client_phone}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    {editMode && !isOwnerLimited ? (
                      <Input
                        value={editData.client_email || ""}
                        onChange={(e) => setEditData({...editData, client_email: e.target.value})}
                        data-testid="edit-client-email"
                        disabled={!!contract.client_id}
                      />
                    ) : (
                      <p className="font-medium">{contract.client_email}</p>
                    )}
                  </div>
                  
                  {/* Show CRM-specific fields if available */}
                  {contract.crm_client && (
                    <div className="pt-3 border-t space-y-2">
                      {contract.crm_client.client_type === "individual" ? (
                        <>
                          <div>
                            <p className="text-sm text-slate-500">CURP</p>
                            <p className="font-mono text-sm">{contract.crm_client.curp || "-"}</p>
                          </div>
                          {contract.crm_client.idmex && (
                            <div>
                              <p className="text-sm text-slate-500">IDMEX</p>
                              <p className="font-mono text-sm">{contract.crm_client.idmex}</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <div>
                          <p className="text-sm text-slate-500">RFC</p>
                          <p className="font-mono text-sm">{contract.crm_client.rfc || "-"}</p>
                        </div>
                      )}
                      {contract.crm_client.address && (
                        <div>
                          <p className="text-sm text-slate-500">Domicilio</p>
                          <p className="text-sm">{contract.crm_client.address}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Service Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Servicio
                    {editMode && <Badge className="bg-blue-100 text-blue-700 ml-2">Campos operativos</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Edit mode warnings */}
                  {editMode && (
                    <div className="space-y-2 mb-4">
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                        Cambios operativos pueden afectar logística y disponibilidad.
                      </div>
                      {hasPayments && (
                        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                          ⚠️ Este contrato tiene pagos registrados.
                        </div>
                      )}
                      {hasLogisticsAssignment && (
                        <div className="p-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-700">
                          ⚠️ Este contrato tiene asignación logística (unidad/chofer).
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-500">Tipo de Servicio</p>
                    {editMode ? (
                      <Select
                        value={editData.service_type || "transfer"}
                        onValueChange={(value) => setEditData({...editData, service_type: value})}
                      >
                        <SelectTrigger data-testid="edit-service-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="transfer">Transfer</SelectItem>
                          <SelectItem value="aeropuerto">Aeropuerto</SelectItem>
                          <SelectItem value="hotel">Hotel</SelectItem>
                          <SelectItem value="disposicion">Disposición</SelectItem>
                          <SelectItem value="tour">Tour</SelectItem>
                          <SelectItem value="especial">Especial</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline">{serviceTypes[contract.service_type]}</Badge>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Origen</p>
                    {editMode ? (
                      <Input
                        value={editData.origin || ""}
                        onChange={(e) => setEditData({...editData, origin: e.target.value})}
                        data-testid="edit-origin"
                      />
                    ) : (
                      <p className="font-medium">{contract.origin}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Destino</p>
                    {editMode ? (
                      <Input
                        value={editData.destination || ""}
                        onChange={(e) => setEditData({...editData, destination: e.target.value})}
                        data-testid="edit-destination"
                      />
                    ) : (
                      <p className="font-medium">{contract.destination}</p>
                    )}
                  </div>
                  {/* Dates */}
                  {editMode && (
                    <>
                      <div>
                        <p className="text-sm text-slate-500">Fecha/Hora Inicio</p>
                        <Input
                          type="datetime-local"
                          value={editData.pickup_datetime || ""}
                          onChange={(e) => setEditData({...editData, pickup_datetime: e.target.value})}
                          data-testid="edit-pickup-datetime"
                        />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Fecha/Hora Fin</p>
                        <Input
                          type="datetime-local"
                          value={editData.end_datetime || ""}
                          onChange={(e) => setEditData({...editData, end_datetime: e.target.value})}
                          min={editData.pickup_datetime}
                          data-testid="edit-end-datetime"
                        />
                      </div>
                    </>
                  )}
                  <div className="flex gap-6">
                    <div>
                      <p className="text-sm text-slate-500">Pasajeros {editMode && !isOwnerLimited && <span className="text-xs text-slate-400">(Opcional)</span>}</p>
                      {editMode && !isOwnerLimited ? (
                        <Input
                          type="number"
                          min="0"
                          placeholder="Opcional"
                          value={editData.passengers || ""}
                          onChange={(e) => setEditData({...editData, passengers: e.target.value ? parseInt(e.target.value) : ""})}
                          className="w-24"
                          data-testid="edit-passengers"
                        />
                      ) : (
                        <p className="font-medium flex items-center gap-1">
                          <Users className="w-4 h-4" /> {contract.passengers || "N/A"}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Maletas {editMode && !isOwnerLimited && <span className="text-xs text-slate-400">(Opcional)</span>}</p>
                      {editMode && !isOwnerLimited ? (
                        <Input
                          type="number"
                          min="0"
                          placeholder="Opcional"
                          value={editData.luggage || ""}
                          onChange={(e) => setEditData({...editData, luggage: e.target.value ? parseInt(e.target.value) : ""})}
                          className="w-24"
                          data-testid="edit-luggage"
                        />
                      ) : (
                        contract.luggage > 0 ? (
                          <p className="font-medium flex items-center gap-1">
                            <Briefcase className="w-4 h-4" /> {contract.luggage}
                          </p>
                        ) : (
                          <p className="text-sm text-slate-400">-</p>
                        )
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Vuelo {editMode && !isOwnerLimited && <span className="text-xs text-slate-400">(Opcional)</span>}</p>
                    {editMode && !isOwnerLimited ? (
                      <Input
                        placeholder="Ej: AA1234"
                        value={editData.flight_number || ""}
                        onChange={(e) => setEditData({...editData, flight_number: e.target.value})}
                        className="w-32"
                        data-testid="edit-flight-number"
                      />
                    ) : (
                      contract.flight_number ? (
                        <p className="font-medium flex items-center gap-1">
                          <Plane className="w-4 h-4" /> {contract.flight_number}
                        </p>
                      ) : (
                        <p className="text-sm text-slate-400">Sin vuelo</p>
                      )
                    )}
                  </div>
                  
                  {/* Full Itinerary - NEW */}
                  {(editMode || contract.full_itinerary) && (
                    <div>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Route className="w-3 h-3" />
                        Itinerario Completo
                      </p>
                      {editMode ? (
                        <Textarea
                          placeholder="Describe el itinerario completo del viaje..."
                          value={editData.full_itinerary || ""}
                          onChange={(e) => setEditData({...editData, full_itinerary: e.target.value})}
                          rows={3}
                          className="mt-1"
                          data-testid="edit-full-itinerary"
                        />
                      ) : (
                        <p className="text-sm mt-1 p-2 bg-slate-50 rounded whitespace-pre-wrap">
                          {contract.full_itinerary}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Special Requirements */}
                  {(editMode || contract.special_requirements) && (
                    <div>
                      <p className="text-sm text-slate-500">Requerimientos Especiales</p>
                      {editMode ? (
                        <Textarea
                          placeholder="Sillas de bebé, wifi, etc..."
                          value={editData.special_requirements || ""}
                          onChange={(e) => setEditData({...editData, special_requirements: e.target.value})}
                          rows={2}
                          className="mt-1"
                          data-testid="edit-special-requirements"
                        />
                      ) : (
                        <p className="text-sm mt-1 p-2 bg-amber-50 rounded">
                          {contract.special_requirements}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Financial Info — hidden for owner-limited (Fase 1) */}
              {!isLogistica && !isOwnerLimited && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Financiero
                      {editMode && <Badge className="bg-amber-100 text-amber-700 ml-2">Campos sensibles</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Vehicle Lines Section - NEW */}
                    {editMode ? (
                      <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                          <Truck className="w-4 h-4" />
                          Unidades Contratadas
                        </p>
                        
                        {/* Add new vehicle line */}
                        <div className="flex flex-wrap items-end gap-2 p-2 bg-white rounded border">
                          <div className="space-y-1">
                            <Label className="text-xs">Tipo</Label>
                            <Select
                              value={newVehicleLine.type}
                              onValueChange={(value) => {
                                const vt = vehicleTypes.find(t => t.code === value);
                                setNewVehicleLine(prev => ({ 
                                  ...prev, 
                                  type: value, 
                                  capacity: vt?.capacity || 4 
                                }));
                              }}
                            >
                              <SelectTrigger className="w-36" data-testid="new-vehicle-type">
                                <SelectValue placeholder="Tipo..." />
                              </SelectTrigger>
                              <SelectContent>
                                {vehicleTypes.map(vt => (
                                  <SelectItem key={vt.code} value={vt.code}>
                                    {vt.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Cantidad</Label>
                            <Input
                              type="number"
                              min="1"
                              max="10"
                              className="w-16"
                              value={newVehicleLine.quantity}
                              onChange={(e) => setNewVehicleLine(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Precio c/u</Label>
                            <Input
                              type="number"
                              min="0"
                              step="any"
                              className="w-24"
                              placeholder="0"
                              value={newVehicleLine.base_price || ""}
                              onChange={(e) => setNewVehicleLine(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                            />
                          </div>
                          <Button 
                            type="button" 
                            onClick={handleAddVehicleLineEdit} 
                            size="sm" 
                            disabled={!newVehicleLine.type}
                            className="h-9"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Vehicle lines list */}
                        {(editData.vehicle_lines || []).length > 0 && (
                          <div className="space-y-2">
                            {(editData.vehicle_lines || []).map((v, i) => {
                              const typeName = vehicleTypes.find(t => t.code === v.type)?.name || v.type;
                              const isExpanded = expandedEditLineIds.includes(v.id);
                              const hasCustomIntent = !!(v.line_start_datetime || v.line_end_datetime || v.line_origin || v.line_destination || v.line_notes);
                              const hasActiveV2 = activeV2LineIds.includes(v.id);
                              const windowLocked = hasActiveV2;
                              return (
                                <div key={v.id || i} className="bg-white rounded border" data-testid={`edit-vehicle-line-row-${i}`}>
                                  <div className="flex items-center gap-2 p-2">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => toggleEditLineExpand(v.id)}
                                      className="h-8 w-8 p-0"
                                      data-testid={`edit-toggle-line-intent-${i}`}
                                      title="Mostrar/ocultar intención operativa de esta línea"
                                    >
                                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </Button>
                                    <span className="text-sm flex-1">
                                      {i + 1}. {typeName} ({v.capacity} pax)
                                      {hasActiveV2 ? (
                                        <Badge variant="outline" className="ml-2 text-[10px] border-emerald-300 text-emerald-700" data-testid={`edit-line-badge-v2-${i}`}>
                                          v2 asignada
                                        </Badge>
                                      ) : hasCustomIntent ? (
                                        <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 text-amber-700" data-testid={`edit-line-badge-custom-${i}`}>
                                          Personalizada
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline" className="ml-2 text-[10px] border-slate-300 text-slate-500" data-testid={`edit-line-badge-inherit-${i}`}>
                                          Hereda
                                        </Badge>
                                      )}
                                    </span>
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-slate-500">$</span>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={v.base_price || 0}
                                        onChange={(e) => handleUpdateVehicleLinePriceEdit(v.id, e.target.value)}
                                        className="w-24 h-8 text-sm"
                                        data-testid={`edit-line-base-price-${i}`}
                                      />
                                    </div>
                                    <Button 
                                      type="button" 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => handleRemoveVehicleLineEdit(v.id)} 
                                      className="h-8 w-8 p-0"
                                      data-testid={`edit-remove-line-${i}`}
                                    >
                                      <X className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                  {isExpanded && (
                                    <div className="border-t bg-slate-50/70 p-3 space-y-3" data-testid={`edit-line-intent-form-${i}`}>
                                      <div className="flex items-start gap-2 text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded p-2">
                                        <Info className="w-3.5 h-3.5 mt-0.5 text-blue-500 flex-shrink-0" />
                                        <span>
                                          Intención operativa de esta línea (opcional). Si dejas un campo vacío, hereda del contrato global.
                                          Las fechas operativas no pueden quedar fuera del rango global del contrato.
                                        </span>
                                      </div>
                                      {windowLocked && (
                                        <div className="flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid={`edit-line-v2-lock-${i}`}>
                                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />
                                          <span>
                                            Esta línea ya tiene unidad/chofer asignado. Para cambiar su horario operativo, primero elimina o actualiza la asignación. Puedes editar origen, destino y notas.
                                          </span>
                                        </div>
                                      )}
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                          <Label className="text-xs">Inicio operativo (línea)</Label>
                                          <Input
                                            type="datetime-local"
                                            value={v.line_start_datetime || ""}
                                            disabled={windowLocked}
                                            min={editData.pickup_datetime || undefined}
                                            max={editData.end_datetime || undefined}
                                            onChange={(e) => updateLineIntentFieldEdit(v.id, "line_start_datetime", e.target.value || null)}
                                            className="h-8 text-sm"
                                            data-testid={`edit-line-start-${i}`}
                                          />
                                        </div>
                                        <div>
                                          <Label className="text-xs">Fin operativo (línea)</Label>
                                          <Input
                                            type="datetime-local"
                                            value={v.line_end_datetime || ""}
                                            disabled={windowLocked}
                                            min={v.line_start_datetime || editData.pickup_datetime || undefined}
                                            max={editData.end_datetime || undefined}
                                            onChange={(e) => updateLineIntentFieldEdit(v.id, "line_end_datetime", e.target.value || null)}
                                            className="h-8 text-sm"
                                            data-testid={`edit-line-end-${i}`}
                                          />
                                        </div>
                                        <div>
                                          <Label className="text-xs">Origen específico (línea)</Label>
                                          <Input
                                            type="text"
                                            value={v.line_origin || ""}
                                            onChange={(e) => updateLineIntentFieldEdit(v.id, "line_origin", e.target.value || null)}
                                            placeholder={editData.origin ? `Hereda: ${editData.origin}` : "Hereda del contrato"}
                                            className="h-8 text-sm"
                                            data-testid={`edit-line-origin-${i}`}
                                          />
                                        </div>
                                        <div>
                                          <Label className="text-xs">Destino específico (línea)</Label>
                                          <Input
                                            type="text"
                                            value={v.line_destination || ""}
                                            onChange={(e) => updateLineIntentFieldEdit(v.id, "line_destination", e.target.value || null)}
                                            placeholder={editData.destination ? `Hereda: ${editData.destination}` : "Hereda del contrato"}
                                            className="h-8 text-sm"
                                            data-testid={`edit-line-destination-${i}`}
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <Label className="text-xs">Notas operativas (línea)</Label>
                                        <Textarea
                                          rows={2}
                                          value={v.line_notes || ""}
                                          onChange={(e) => updateLineIntentFieldEdit(v.id, "line_notes", e.target.value || null)}
                                          placeholder="Indicaciones específicas para esta unidad…"
                                          className="text-sm"
                                          data-testid={`edit-line-notes-${i}`}
                                        />
                                      </div>
                                      <div className="flex justify-end">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          disabled={windowLocked}
                                          onClick={() => inheritGlobalForLineEdit(v.id)}
                                          className="h-7 text-xs"
                                          data-testid={`edit-inherit-line-${i}`}
                                        >
                                          <Undo2 className="w-3 h-3 mr-1" /> Heredar datos globales
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            <div className="flex justify-end pt-2 border-t">
                              <span className="text-sm font-medium text-blue-700">
                                Total unidades: ${calculateEditTotals().vehicleLinesTotal.toLocaleString("es-MX")} {editData.currency || "MXN"}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {(editData.vehicle_lines || []).length === 0 && (
                          <p className="text-xs text-amber-600">* El contrato usa precio global. Agregue unidades para usar precios individuales.</p>
                        )}
                        
                        {hasPayments && (
                          <p className="text-xs text-amber-600">⚠️ Modificar unidades requiere razón de cambio</p>
                        )}
                      </div>
                    ) : (
                      /* Display mode: Show vehicle lines if they exist */
                      (contract.vehicle_lines && contract.vehicle_lines.length > 0) && (
                        <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500 flex items-center gap-2">
                            <Truck className="w-4 h-4" />
                            Unidades ({contract.vehicle_lines.length})
                          </p>
                          {contract.vehicle_lines.map((v, i) => {
                            // Support both legacy (vehicle_type) and new (type) field names
                            const vType = v.type || v.vehicle_type;
                            const typeName = vehicleTypes.find(t => t.code === vType)?.name || getVehicleTypeLabel(vType);
                            return (
                              <div key={v.id || i} className="flex justify-between items-center text-sm">
                                <span>{i + 1}. {typeName} ({v.capacity || 4} pax)</span>
                                <span className="font-medium">${(v.base_price || 0).toLocaleString("es-MX")}</span>
                              </div>
                            );
                          })}
                        </div>
                      )
                    )}
                    
                    {/* Base Price - only show if no vehicle_lines or legacy */}
                    {(!editMode && (!contract.vehicle_lines || contract.vehicle_lines.length === 0)) && (
                      <div>
                        <p className="text-sm text-slate-500">Precio Base</p>
                        <p className="font-medium">${contract.base_price?.toLocaleString("es-MX")}</p>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-sm text-slate-500">Extras</p>
                      {editMode ? (
                        <div className="space-y-1">
                          <Input
                            type="number"
                            step="any"
                            value={Array.isArray(editData.extras) ? editData.extras.reduce((s, e) => s + (e.amount || 0), 0) : (editData.extras || "")}
                            onChange={(e) => setEditData({...editData, extras: parseFloat(e.target.value) || 0})}
                            placeholder="Monto total de extras"
                            data-testid="edit-extras"
                          />
                        </div>
                      ) : (
                        contract.extras?.length > 0 ? (
                          contract.extras.map((extra, i) => (
                            <p key={i} className="text-sm">{extra.name}: ${extra.amount?.toLocaleString("es-MX")}</p>
                          ))
                        ) : (
                          <p className="text-sm text-slate-400">Sin extras</p>
                        )
                      )}
                    </div>
                    {editMode && (
                      <>
                        <div>
                          <p className="text-sm text-slate-500">Moneda</p>
                          <Select
                            value={editData.currency || "MXN"}
                            onValueChange={(value) => setEditData({...editData, currency: value})}
                            disabled={hasPayments && !isAdmin}
                          >
                            <SelectTrigger data-testid="edit-currency">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="MXN">MXN - Peso Mexicano</SelectItem>
                              <SelectItem value="USD">USD - Dólar</SelectItem>
                            </SelectContent>
                          </Select>
                          {hasPayments && !isAdmin && (
                            <p className="text-xs text-red-600">🔒 Bloqueado por pagos existentes</p>
                          )}
                        </div>
                        {editData.currency === "USD" && (
                          <div>
                            <p className="text-sm text-slate-500">Tipo de Cambio</p>
                            <Input
                              type="number"
                              step="any"
                              value={editData.exchange_rate || ""}
                              onChange={(e) => setEditData({...editData, exchange_rate: parseFloat(e.target.value) || 1})}
                              disabled={hasPayments && !isAdmin}
                              data-testid="edit-exchange-rate"
                            />
                            {hasPayments && !isAdmin && (
                              <p className="text-xs text-red-600">🔒 Bloqueado por pagos existentes</p>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="edit-requires-invoice"
                            checked={editData.requires_invoice || false}
                            onChange={(e) => setEditData({...editData, requires_invoice: e.target.checked})}
                            className="rounded border-slate-300"
                          />
                          <Label htmlFor="edit-requires-invoice" className="text-sm">Requiere Factura (IVA 16%)</Label>
                        </div>
                        
                        {/* Show calculated totals in edit mode */}
                        <div className="pt-2 border-t bg-green-50 -mx-3 px-3 py-2 rounded-b">
                          <p className="text-xs text-slate-500 mb-1">Vista previa del total:</p>
                          <p className="text-lg font-bold text-green-700">
                            ${calculateEditTotals().total.toLocaleString("es-MX")} {editData.currency || "MXN"}
                          </p>
                          {editData.requires_invoice && (
                            <p className="text-xs text-slate-500">
                              (Subtotal: ${calculateEditTotals().subtotal.toLocaleString("es-MX")} + IVA: ${calculateEditTotals().vatAmount.toLocaleString("es-MX")})
                            </p>
                          )}
                        </div>
                      </>
                    )}
                    {!editMode && (
                      <>
                        <div className="pt-2 border-t">
                          <p className="text-sm text-slate-500">Total</p>
                          <p className="text-2xl font-bold text-slate-900">${contract.total_price?.toLocaleString("es-MX")}</p>
                        </div>
                        <div className="flex gap-4">
                          <div>
                            <p className="text-sm text-slate-500">Pagado</p>
                            <p className="font-medium text-green-600">${totalPaid.toLocaleString("es-MX")}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Saldo</p>
                            <p className={cn("font-medium", balance > 0 ? "text-red-600" : "text-green-600")}>
                              ${balance.toLocaleString("es-MX")}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Date/Time and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Fecha y Hora
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-slate-500 mb-0.5">Inicio</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {new Date(contract.pickup_datetime).toLocaleDateString("es-MX", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric"
                        })}
                      </p>
                      <p className="text-xl text-slate-600">
                        {new Date(contract.pickup_datetime).toLocaleTimeString("es-MX", {
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </p>
                    </div>
                    {contract.end_datetime && (
                      <div className="pt-2 border-t border-slate-100" data-testid="contract-end-datetime">
                        <p className="text-xs text-slate-500 mb-0.5">Terminación</p>
                        <p className="text-lg font-semibold text-slate-900">
                          {new Date(contract.end_datetime).toLocaleDateString("es-MX", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric"
                          })}
                        </p>
                        <p className="text-base text-slate-600">
                          {new Date(contract.end_datetime).toLocaleTimeString("es-MX", {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading">Estado</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Badge className="text-lg px-4 py-2">{statusLabels[contract.status]}</Badge>
                  
                  {canManageAssignments && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant={contract.status === "en_curso" ? "default" : "outline"}
                        onClick={() => handleStatusChange("en_curso")}
                      >
                        En Curso
                      </Button>
                      <Button
                        size="sm"
                        variant={contract.status === "completado" ? "default" : "outline"}
                        onClick={() => handleStatusChange("completado")}
                      >
                        Completado
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleStatusChange("cancelado")}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusChange("no_show")}
                      >
                        No Show
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* v4.59.0: Líneas operativas del contrato (intención por línea, read-only en Resumen) */}
            {Array.isArray(contract.vehicle_lines) && contract.vehicle_lines.length > 0 && (
              <Card data-testid="operational-lines-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Truck className="w-5 h-5" />
                    Líneas operativas del contrato
                    <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600 ml-2">
                      Intención por línea
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded p-2 flex items-start gap-2">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-blue-500 flex-shrink-0" />
                    <span>
                      Esta sección muestra la <strong>intención operativa</strong> capturada por línea (horario, ruta, notas).
                      Alimenta el indicador de presión por tipo. <strong>PDFs, Órdenes de Servicio, Calendario y Dashboard Logística</strong> siguen usando las fechas globales del contrato.
                    </span>
                  </div>
                  {hasV2Assignments && (
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2" data-testid="v2-precedence-banner">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-600 flex-shrink-0" />
                      <span>
                        Este contrato ya tiene <strong>asignaciones reales (v2)</strong>. Para fines de disponibilidad y conflictos, las asignaciones v2 tienen <strong>precedencia</strong> sobre los campos de intención por línea.
                      </span>
                    </div>
                  )}
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                    {contract.vehicle_lines.map((vl, idx) => {
                      const tName = vehicleTypes.find(t => t.code === (vl.type || vl.vehicle_type))?.name || getVehicleTypeLabel(vl.type || vl.vehicle_type);
                      const windowInherited = vl.line_window_inherited !== false; // default true
                      const originInherited = vl.line_origin_inherited !== false;
                      const destInherited = vl.line_destination_inherited !== false;
                      const fmtDT = (d) => d ? new Date(d).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }) : "—";
                      return (
                        <div key={vl.id || idx} className="p-3 bg-white" data-testid={`operational-line-${idx}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{idx + 1}. {tName} ({vl.capacity || 4} pax)</span>
                              {vl.assigned_vehicle_id && (
                                <Badge className="bg-green-100 text-green-800 text-[10px]">Asignada</Badge>
                              )}
                            </div>
                            <span className="text-xs text-slate-500">ID línea: <code className="text-[10px]">{(vl.id || "").substring(0, 8)}</code></span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3 h-3 text-slate-400" />
                              <span className="text-slate-500">Inicio:</span>
                              <span className="font-medium">{fmtDT(vl.effective_line_start_datetime)}</span>
                              <Badge variant="outline" className={cn("text-[9px] ml-1", windowInherited ? "border-slate-300 text-slate-600" : "border-amber-300 text-amber-700")}>
                                {windowInherited ? "Hereda" : "Personalizada"}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3 h-3 text-slate-400" />
                              <span className="text-slate-500">Fin:</span>
                              <span className="font-medium">{fmtDT(vl.effective_line_end_datetime)}</span>
                              <Badge variant="outline" className={cn("text-[9px] ml-1", windowInherited ? "border-slate-300 text-slate-600" : "border-amber-300 text-amber-700")}>
                                {windowInherited ? "Hereda" : "Personalizada"}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-3 h-3 text-slate-400" />
                              <span className="text-slate-500">Origen:</span>
                              <span className="font-medium truncate">{vl.effective_line_origin || "—"}</span>
                              <Badge variant="outline" className={cn("text-[9px] ml-1", originInherited ? "border-slate-300 text-slate-600" : "border-amber-300 text-amber-700")}>
                                {originInherited ? "Hereda" : "Personalizada"}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-3 h-3 text-slate-400" />
                              <span className="text-slate-500">Destino:</span>
                              <span className="font-medium truncate">{vl.effective_line_destination || "—"}</span>
                              <Badge variant="outline" className={cn("text-[9px] ml-1", destInherited ? "border-slate-300 text-slate-600" : "border-amber-300 text-amber-700")}>
                                {destInherited ? "Hereda" : "Personalizada"}
                              </Badge>
                            </div>
                          </div>
                          {vl.line_notes && (
                            <div className="mt-2 text-xs text-slate-600 italic bg-slate-50 p-2 rounded border border-slate-100">
                              <strong>Notas de línea:</strong> {vl.line_notes}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          <TabsContent value="operacion" className="space-y-6">
            {/* Multiple Assignments Component (New v2) */}
            <MultipleAssignments 
              contractId={id}
              contract={contract}
              canManage={canManageAssignments}
              onUpdate={fetchData}
            />

            {/* Legacy Single Assignment Display (for backwards compatibility) */}
            {(contract.vehicle_id || contract.driver_id) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <Truck className="w-5 h-5" />
                      Asignación Legacy (v1)
                      <Badge className="bg-amber-100 text-amber-800 text-xs">Migrar a v2</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {vehicle ? (
                      <div className="space-y-2">
                        <p className="text-xl font-bold">{vehicle.name}</p>
                        <p className="text-slate-500">Placas: {vehicle.plate}</p>
                        <p className="text-slate-500">Tipo: {vehicle.vehicle_type}</p>
                        <p className="text-slate-500">Capacidad: {vehicle.capacity} pasajeros</p>
                      </div>
                    ) : (
                      <p className="text-slate-500">Sin unidad asignada (legacy)</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Chofer Legacy (v1)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {driver ? (
                      <div className="space-y-2">
                        <p className="text-xl font-bold">{driver.name}</p>
                        <p className="text-slate-500">Teléfono: {driver.phone}</p>
                        <p className="text-slate-500">Licencia: {driver.license_number}</p>
                      </div>
                    ) : (
                      <p className="text-slate-500">Sin chofer asignado (legacy)</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Notas</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-500">Notas Internas (Logística)</Label>
                  <p className="mt-1 p-3 bg-slate-50 rounded-lg min-h-[80px]">
                    {contract.internal_notes || "Sin notas internas"}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Notas para Cliente</Label>
                  <p className="mt-1 p-3 bg-slate-50 rounded-lg min-h-[80px]">
                    {contract.customer_notes || "Sin notas para cliente"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pagos Tab */}
          <TabsContent value="pagos" className="space-y-6">
            {/* Payment Summary Card - Enhanced with Status */}
            {paymentSummary && (
              <Card className={cn(
                "border-2",
                paymentSummary.fully_paid 
                  ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-300"
                  : paymentSummary.deposit_met
                    ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300"
                    : "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300"
              )}>
                <CardContent className="p-6">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      {paymentSummary.fully_paid ? (
                        <Badge className="bg-green-600 text-white text-sm px-3 py-1">
                          <Check className="w-4 h-4 mr-1" />
                          PAGADO
                        </Badge>
                      ) : paymentSummary.deposit_met ? (
                        <Badge className="bg-blue-600 text-white text-sm px-3 py-1">
                          <Check className="w-4 h-4 mr-1" />
                          CONFIRMADO
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-600 text-white text-sm px-3 py-1">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          PENDIENTE ANTICIPO
                        </Badge>
                      )}
                    </div>
                    <span className="text-sm text-slate-500">
                      Moneda: <span className="font-medium">{paymentSummary.currency || "MXN"}</span>
                    </span>
                  </div>

                  {/* Amounts Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                      <p className="text-sm text-slate-500">Total del Contrato</p>
                      <p className="text-xl font-bold">
                        {paymentSummary.currency === "USD" ? "USD " : ""}${paymentSummary.total_amount?.toLocaleString("es-MX")}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Anticipo Requerido (20%)</p>
                      <p className={cn(
                        "text-xl font-bold flex items-center gap-1",
                        paymentSummary.deposit_met ? "text-green-600" : "text-amber-600"
                      )}>
                        ${paymentSummary.deposit_required?.toLocaleString("es-MX")}
                        {paymentSummary.deposit_met && <Check className="w-5 h-5" />}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Pagado</p>
                      <p className="text-xl font-bold text-blue-600">
                        ${paymentSummary.total_paid?.toLocaleString("es-MX")}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Saldo Pendiente</p>
                      <p className={cn(
                        "text-xl font-bold",
                        paymentSummary.balance > 0 ? "text-red-600" : "text-green-600"
                      )}>
                        ${paymentSummary.balance?.toLocaleString("es-MX")}
                      </p>
                    </div>
                  </div>

                  {/* Deposit Progress Bar (only if not fully paid) */}
                  {!paymentSummary.fully_paid && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">Progreso del anticipo</span>
                        <span className="font-medium">
                          {Math.min(100, Math.round((paymentSummary.total_paid / paymentSummary.deposit_required) * 100))}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className={cn(
                            "h-2 rounded-full transition-all",
                            paymentSummary.deposit_met ? "bg-green-500" : "bg-amber-500"
                          )}
                          style={{ 
                            width: `${Math.min(100, (paymentSummary.total_paid / paymentSummary.deposit_required) * 100)}%` 
                          }}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Contextual Payment Actions */}
            {paymentSummary && (
              <div className="space-y-4">
                {/* CASO 1: Contrato PAGADO */}
                {paymentSummary.fully_paid || paymentSummary.balance <= 0 ? (
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-6 text-center">
                      <Check className="w-12 h-12 mx-auto mb-3 text-green-600" />
                      <h3 className="text-xl font-bold text-green-800">Contrato Liquidado</h3>
                      <p className="text-green-600 mt-1">
                        Este contrato ha sido pagado en su totalidad
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* RBAC: Solo admin puede ver acciones de pago */}
                    {isAdmin ? (
                      <>
                        {/* CASO 2: PENDIENTE_ANTICIPO (deposit_met = false) */}
                        {!paymentSummary.deposit_met ? (
                          <div className="space-y-3">
                            {/* Warning Alert */}
                            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                              <div className="text-sm">
                                <span className="font-medium text-amber-800">Anticipo pendiente:</span>
                                <span className="text-amber-700 ml-1">
                                  Faltan ${(paymentSummary.deposit_required - paymentSummary.total_paid).toLocaleString("es-MX")} para completar el anticipo mínimo (20%)
                                </span>
                              </div>
                            </div>

                            {/* Primary Action: Deposit */}
                            <div className="flex flex-wrap gap-3">
                              <Button 
                                onClick={() => handleStripePayment("deposit")}
                                disabled={stripeLoading}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid="stripe-deposit-btn"
                              >
                                {stripeLoading ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <CreditCard className="w-4 h-4 mr-2" />
                                )}
                                Generar link de anticipo (${(paymentSummary.deposit_required - paymentSummary.total_paid).toLocaleString("es-MX")})
                              </Button>
                              
                              {/* Secondary: Custom Amount */}
                              <Button 
                                variant="outline"
                                onClick={() => openStripeDialog("custom")}
                                disabled={stripeLoading}
                                className="border-slate-300"
                            data-testid="stripe-custom-btn"
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pago personalizado
                          </Button>
                          
                          {/* Tertiary: Pay Total */}
                          <Button 
                            variant="outline"
                            onClick={() => handleStripePayment("balance")}
                            disabled={stripeLoading}
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            data-testid="stripe-balance-btn"
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pagar total (${paymentSummary.balance.toLocaleString("es-MX")})
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* CASO 3: CONFIRMADO (deposit_met = true, balance > 0) */
                      <div className="space-y-3">
                        {/* Info Alert */}
                        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          <div className="text-sm">
                            <span className="font-medium text-blue-800">Anticipo completado.</span>
                            <span className="text-blue-700 ml-1">
                              Saldo pendiente: ${paymentSummary.balance.toLocaleString("es-MX")}
                            </span>
                          </div>
                        </div>

                        {/* Primary Action: Pay Balance */}
                        <div className="flex flex-wrap gap-3">
                          <Button 
                            onClick={() => handleStripePayment("balance")}
                            disabled={stripeLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid="stripe-balance-btn"
                          >
                            {stripeLoading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CreditCard className="w-4 h-4 mr-2" />
                            )}
                            Generar link de saldo pendiente (${paymentSummary.balance.toLocaleString("es-MX")})
                          </Button>
                          
                          {/* Secondary: Custom Amount */}
                          <Button 
                            variant="outline"
                            onClick={() => openStripeDialog("custom")}
                            disabled={stripeLoading}
                            className="border-slate-300"
                            data-testid="stripe-custom-btn"
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pago personalizado
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Manual Payment Button - Always visible when balance > 0 */}
                    <div className="pt-2 border-t border-slate-200">
                      <Button 
                        variant="ghost" 
                        onClick={() => setPaymentDialogOpen(true)} 
                        className="text-slate-600"
                        data-testid="manual-payment-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Registrar Pago Manual (efectivo, transferencia, terminal)
                      </Button>
                    </div>
                  </>
                      ) : (
                        /* Vista para NO ADMIN */
                        <Card className="bg-slate-50 border-slate-200">
                          <CardContent className="p-6">
                            <div className="flex items-center gap-3">
                              <AlertCircle className="w-8 h-8 text-slate-400" />
                              <div>
                                <h3 className="font-medium text-slate-700">Saldo pendiente: ${paymentSummary.balance?.toLocaleString("es-MX")}</h3>
                                <p className="text-sm text-slate-500">Solo administración puede registrar o validar pagos</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </>
                )}
              </div>
            )}

            {/* Flexible Payment Link generator (Fase Ligas) */}
            {!isLogistica && user?.role !== "mantenimiento" && (
              <Card data-testid="payment-links-section">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <Link2Icon className="w-4 h-4 text-blue-600" />
                      Ligas de pago
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">Genera ligas Stripe en cualquier momento del ciclo</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setLinkDialogOpen(true)}
                    data-testid="open-payment-link-dialog"
                  >
                    <Link2Icon className="w-4 h-4 mr-1" /> Generar liga
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  {paymentLinksLoading ? (
                    <div className="p-6 text-center text-sm text-slate-400">Cargando…</div>
                  ) : paymentLinks.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-500">
                      Aún no se han generado ligas de pago para este contrato.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead className="text-right">Monto</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Generado por</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paymentLinks.map((l) => {
                          const statusStyle =
                            l.status === "paid"
                              ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                              : l.status === "expired"
                                ? "bg-slate-100 text-slate-600 border-slate-300"
                                : "bg-amber-100 text-amber-800 border-amber-300";
                          return (
                            <TableRow key={l.id || l.session_id} data-testid={`link-row-${l.id || l.session_id}`}>
                              <TableCell className="text-xs">
                                {l.created_at ? new Date(l.created_at).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">
                                  {l.description || l.payment_type}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {l.currency === "USD" ? "USD " : ""}${(l.amount || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={cn("text-[10px] uppercase", statusStyle)}>{l.status}</Badge>
                              </TableCell>
                              <TableCell className="text-xs text-slate-600">{l.created_by_name || "—"}</TableCell>
                              <TableCell className="text-right">
                                {l.status === "pending" && l.session_id && (
                                  <span className="text-[10px] text-slate-400 italic">vence en 48h</span>
                                )}
                                {l.status === "paid" && l.completed_at && (
                                  <span className="text-[10px] text-emerald-600">
                                    pagado {new Date(l.completed_at).toLocaleDateString("es-MX")}
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Payments History */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading">Historial de Pagos</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {payments.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay pagos registrados</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Método</TableHead>
                        <TableHead>Referencia</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        {isAdmin && <TableHead className="text-right w-20">Acciones</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                          <TableCell>
                            {new Date(payment.created_at).toLocaleDateString("es-MX")}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={cn(
                                payment.payment_type === "DEPOSIT" && "border-amber-300 text-amber-700 bg-amber-50",
                                payment.payment_type === "FINAL" && "border-green-300 text-green-700 bg-green-50",
                                payment.payment_type === "PARTIAL" && "border-blue-300 text-blue-700 bg-blue-50"
                              )}
                            >
                              {payment.payment_type === "DEPOSIT" ? "Anticipo" : 
                               payment.payment_type === "FINAL" ? "Final" : "Parcial"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {payment.method === "STRIPE" && <CreditCard className="w-4 h-4 text-purple-500" />}
                              {payment.method === "TRANSFERENCIA" && <Building2 className="w-4 h-4 text-blue-500" />}
                              {payment.method === "TERMINAL" && <Smartphone className="w-4 h-4 text-green-500" />}
                              {payment.method === "EFECTIVO" && <DollarSign className="w-4 h-4 text-emerald-500" />}
                              <span className="text-sm">{payment.method}</span>
                            </div>
                            {payment.bank_account_info && (
                              <span className="text-xs text-slate-500 block">
                                {payment.bank_account_info.bank_name} ****{payment.bank_account_info.last4}
                              </span>
                            )}
                            {payment.terminal_info && (
                              <span className="text-xs text-slate-500 block">
                                {payment.terminal_info.provider} - {payment.terminal_info.name}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{payment.reference || "—"}</TableCell>
                          <TableCell className="text-right">
                            <span className="font-medium text-green-600">
                              {payment.currency === "USD" ? "USD " : ""}${payment.amount?.toLocaleString("es-MX")}
                            </span>
                            {payment.currency === "USD" && payment.amount_mxn && (
                              <span className="block text-xs text-slate-500">
                                MXN ${payment.amount_mxn?.toLocaleString("es-MX")}
                              </span>
                            )}
                          </TableCell>
                          {isAdmin && (
                            <TableCell className="text-right">
                              {payment.reconciled_status === "RECONCILED" ? (
                                <Badge variant="outline" className="text-xs border-slate-300 text-slate-500" title="Pago conciliado, no se puede eliminar">
                                  Conciliado
                                </Badge>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => openDeletePaymentDialog(payment)}
                                  data-testid={`delete-payment-btn-${payment.id}`}
                                  title="Eliminar pago"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bitácora Tab - Change Logs */}
          <TabsContent value="bitacora" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Historial de Cambios
                </CardTitle>
              </CardHeader>
              <CardContent>
                {changeLogsError ? (
                  <div className="text-center py-8 text-amber-600 bg-amber-50 rounded-lg">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-400" />
                    <p className="font-medium">No se pudo cargar la bitácora</p>
                    <p className="text-sm text-amber-500 mt-1">El resto del contrato sigue disponible</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3"
                      onClick={() => {
                        setChangeLogsError(null);
                        contractsAPI.getLogs(id).then(res => setChangeLogs(res.data || [])).catch(() => setChangeLogsError("Error"));
                      }}
                    >
                      Reintentar
                    </Button>
                  </div>
                ) : changeLogs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay cambios registrados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {changeLogs.map((log) => (
                      <div key={log.id} className="p-4 border rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="font-mono text-xs">
                                {log.field_name}
                              </Badge>
                              <span className="text-xs text-slate-500">
                                {new Date(log.timestamp).toLocaleString("es-MX")}
                              </span>
                            </div>
                            <div className="mt-2 text-sm">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-slate-500">Antes:</span>
                                <span className="font-mono text-red-600 bg-red-50 px-1 rounded text-xs truncate max-w-[200px]">
                                  {log.old_value || "(vacío)"}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className="text-slate-500">Después:</span>
                                <span className="font-mono text-green-600 bg-green-50 px-1 rounded text-xs truncate max-w-[200px]">
                                  {log.new_value || "(vacío)"}
                                </span>
                              </div>
                            </div>
                            {log.reason && (
                              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                                <span className="font-medium text-amber-800">Razón:</span>
                                <span className="ml-1 text-amber-700">{log.reason}</span>
                              </div>
                            )}
                          </div>
                          <div className="text-right text-sm text-slate-600 flex-shrink-0">
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              <span>{log.changed_by_name || log.changed_by_user_id?.slice(0, 8) + "..."}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Adjuntos Tab - RESTORED v4.10.1 after Trash2 import fix */}
          <TabsContent value="adjuntos" className="space-y-6">
            <SectionErrorBoundary sectionName="Adjuntos">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-dashed">
                <CardContent className="p-6">
                  <Label htmlFor="contract-file" className="cursor-pointer">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <Upload className="w-8 h-8 text-slate-400" />
                      <p className="font-medium">Contrato PDF</p>
                      <p className="text-sm text-slate-500">Subir documento</p>
                    </div>
                  </Label>
                  <Input
                    id="contract-file"
                    type="file"
                    accept=".pdf,.jpg,.png"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, "CONTRACT")}
                  />
                </CardContent>
              </Card>

              <Card className="border-dashed">
                <CardContent className="p-6">
                  <Label htmlFor="ine-file" className="cursor-pointer">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <Upload className="w-8 h-8 text-slate-400" />
                      <p className="font-medium">INE</p>
                      <p className="text-sm text-slate-500">Identificación</p>
                    </div>
                  </Label>
                  <Input
                    id="ine-file"
                    type="file"
                    accept=".pdf,.jpg,.png"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, "INE")}
                  />
                </CardContent>
              </Card>

              <Card className="border-dashed">
                <CardContent className="p-6">
                  <Label htmlFor="proof-file" className="cursor-pointer">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <Upload className="w-8 h-8 text-slate-400" />
                      <p className="font-medium">Comprobante</p>
                      <p className="text-sm text-slate-500">Pago</p>
                    </div>
                  </Label>
                  <Input
                    id="proof-file"
                    type="file"
                    accept=".pdf,.jpg,.png"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, "PAYMENT_PROOF")}
                  />
                </CardContent>
              </Card>
            </div>

            {attachments && attachments.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading">Archivos Adjuntos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {attachments.map((att) => {
                      // Validación defensiva para cada attachment
                      if (!att || !att.id) return null;
                      const contentType = att.content_type || "";
                      const isPreviewable = contentType === "application/pdf" || contentType.startsWith("image/");
                      return (
                        <div key={att.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Paperclip className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="font-medium">{att.filename || "Archivo sin nombre"}</p>
                              <p className="text-sm text-slate-500">{att.type || "Tipo desconocido"}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isPreviewable && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const url = attachmentsAPI.previewUrl(att.id);
                                  const token = localStorage.getItem("token");
                                  // Open preview in new tab with auth
                                  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                                    .then(res => res.blob())
                                    .then(blob => {
                                      const blobUrl = URL.createObjectURL(blob);
                                      window.open(blobUrl, "_blank");
                                    })
                                    .catch(() => toast.error("Error al abrir vista previa"));
                                }}
                                title="Ver archivo"
                                data-testid={`preview-attachment-${att.id}`}
                              >
                                <Eye className="w-4 h-4 text-blue-600" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(att.id, att.filename || "archivo")}
                              title="Descargar"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAttachment(att.id, att.filename || "archivo")}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Eliminar documento"
                              data-testid={`delete-attachment-${att.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            </SectionErrorBoundary>
          </TabsContent>

          {/* Auditoria Tab */}
          {isAdmin && (
            <TabsContent value="auditoria">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Historial de Cambios
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {auditError ? (
                    <div className="text-center py-8 text-amber-600 bg-amber-50 rounded-lg">
                      <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-400" />
                      <p className="font-medium">No se pudo cargar la auditoría</p>
                      <p className="text-sm text-amber-500 mt-1">El resto del contrato sigue disponible</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3"
                        onClick={() => {
                          setAuditError(null);
                          auditAPI.getLogs({ entity_type: "contract", entity_id: id }).then(res => setAuditLogs(res.data || [])).catch(() => setAuditError("Error"));
                        }}
                      >
                        Reintentar
                      </Button>
                    </div>
                  ) : auditLogs.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No hay registros de auditoría</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {auditLogs.map((log) => (
                        <div key={log.id} className="p-4 border rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <Badge variant="outline">{log.action}</Badge>
                              <p className="text-sm text-slate-500 mt-1">
                                {new Date(log.performed_at).toLocaleString("es-MX")}
                              </p>
                            </div>
                            <p className="text-sm text-slate-500">Por: {log.performed_by?.slice(0, 8)}...</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Payment Dialog */}
        <Dialog
          open={paymentDialogOpen}
          onOpenChange={(open) => {
            // Bloquear cierre del modal durante submit (v4.49.0)
            if (!open && paymentSubmitting) return;
            setPaymentDialogOpen(open);
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Registrar Pago</DialogTitle>
            </DialogHeader>
            
            {/* Payment Summary */}
            {paymentSummary && (
              <div className="p-4 bg-slate-50 rounded-lg border space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total del contrato:</span>
                  <span className="font-medium">
                    {paymentSummary.currency === "USD" ? "USD " : ""}${paymentSummary.total_amount?.toLocaleString("es-MX")}
                  </span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Anticipo requerido (20%):</span>
                  <span className="font-medium">
                    ${paymentSummary.deposit_required?.toLocaleString("es-MX")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Pagado:</span>
                  <span className="font-medium text-blue-600">
                    ${paymentSummary.total_paid?.toLocaleString("es-MX")}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-medium">Saldo pendiente:</span>
                  <span className="font-bold text-lg">
                    ${paymentSummary.balance?.toLocaleString("es-MX")}
                  </span>
                </div>
                {paymentSummary.deposit_met && (
                  <div className="flex items-center gap-2 text-green-600 pt-2">
                    <Circle className="w-3 h-3 fill-current" />
                    <span className="text-xs">Anticipo completado</span>
                  </div>
                )}
              </div>
            )}
            
            <form onSubmit={handlePayment} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Monto * {paymentSummary && `(Max: $${paymentSummary.balance?.toLocaleString("es-MX")})`}</Label>
                <Input
                  id="amount"
                  type="number"
                  step="any"
                  min="0"
                  max={paymentSummary?.balance || 999999}
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  required
                  data-testid="payment-amount-input"
                />
                {paymentSummary && !paymentSummary.deposit_met && paymentData.amount >= paymentSummary.deposit_required && (
                  <p className="text-xs text-green-600">✓ Este pago completará el anticipo requerido</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="method">Método de Pago *</Label>
                <Select
                  value={paymentData.method}
                  onValueChange={(value) => setPaymentData(prev => ({ 
                    ...prev, 
                    method: value,
                    bank_account_id: "",
                    terminal_id: ""
                  }))}
                >
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EFECTIVO">💵 Efectivo</SelectItem>
                    <SelectItem value="TRANSFERENCIA">🏦 Transferencia</SelectItem>
                    <SelectItem value="TERMINAL">💳 Terminal Bancaria</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Bank Account Selection (for TRANSFERENCIA) */}
              {paymentData.method === "TRANSFERENCIA" && (
                <div className="space-y-2">
                  <Label>Cuenta Bancaria Destino *</Label>
                  <Select
                    value={paymentData.bank_account_id}
                    onValueChange={(value) => setPaymentData(prev => ({ ...prev, bank_account_id: value }))}
                  >
                    <SelectTrigger data-testid="bank-account-select">
                      <SelectValue placeholder="Seleccionar cuenta..." />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map(account => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name} - {account.bank_name} (****{account.account_number_last4})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {bankAccounts.length === 0 && (
                    <p className="text-xs text-amber-600">No hay cuentas bancarias configuradas. Configure en Configuración → Finanzas</p>
                  )}
                </div>
              )}
              
              {/* Terminal Selection (for TERMINAL) */}
              {paymentData.method === "TERMINAL" && (
                <div className="space-y-2">
                  <Label>Terminal Utilizada *</Label>
                  <Select
                    value={paymentData.terminal_id}
                    onValueChange={(value) => setPaymentData(prev => ({ ...prev, terminal_id: value }))}
                  >
                    <SelectTrigger data-testid="terminal-select">
                      <SelectValue placeholder="Seleccionar terminal..." />
                    </SelectTrigger>
                    <SelectContent>
                      {cardTerminals.map(terminal => (
                        <SelectItem key={terminal.id} value={terminal.id}>
                          {terminal.name} - {terminal.provider}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {cardTerminals.length === 0 && (
                    <p className="text-xs text-amber-600">No hay terminales configuradas. Configure en Configuración → Finanzas</p>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="reference">Referencia / Folio</Label>
                <Input
                  id="reference"
                  value={paymentData.reference}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder={paymentData.method === "TRANSFERENCIA" ? "Folio SPEI" : "Número de autorización"}
                  data-testid="payment-reference-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea
                  id="notes"
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  data-testid="payment-notes-input"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPaymentDialogOpen(false)}
                  disabled={paymentSubmitting}
                  data-testid="cancel-payment-btn"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={paymentSubmitting}
                  data-testid="submit-payment-btn"
                >
                  {paymentSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Registrando pago...
                    </>
                  ) : (
                    "Registrar Pago"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Eliminar Pago Dialog (admin only) — v4.49.0 */}
        <Dialog
          open={deletePaymentDialogOpen}
          onOpenChange={(open) => {
            if (!open && deletePaymentSubmitting) return;
            setDeletePaymentDialogOpen(open);
            if (!open) {
              setPaymentToDelete(null);
              setDeletePaymentReason("");
            }
          }}
        >
          <DialogContent data-testid="delete-payment-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-red-700">¿Eliminar pago?</DialogTitle>
              <DialogDescription>
                Esta acción afectará el total pagado, saldo pendiente, estado de anticipo, CxC, comisiones y reportes financieros relacionados. No debe usarse si el pago ya fue conciliado contablemente.
              </DialogDescription>
            </DialogHeader>
            {paymentToDelete && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Fecha:</span>
                  <span className="font-medium">{new Date(paymentToDelete.created_at).toLocaleString("es-MX")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Método:</span>
                  <span className="font-medium">{paymentToDelete.method}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Referencia:</span>
                  <span className="font-medium">{paymentToDelete.reference || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Monto:</span>
                  <span className="font-semibold text-red-600">
                    {paymentToDelete.currency === "USD" ? "USD " : ""}${paymentToDelete.amount?.toLocaleString("es-MX")}
                  </span>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="delete-payment-reason">
                Motivo de eliminación <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="delete-payment-reason"
                value={deletePaymentReason}
                onChange={(e) => setDeletePaymentReason(e.target.value)}
                placeholder="Ej: Pago duplicado por doble click accidental"
                rows={3}
                disabled={deletePaymentSubmitting}
                data-testid="delete-payment-reason-input"
              />
              <p className="text-xs text-slate-500">
                Mínimo 10 caracteres. Quedará registrado en la bitácora de auditoría.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeletePaymentDialogOpen(false)}
                disabled={deletePaymentSubmitting}
                data-testid="cancel-delete-payment-btn"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeletePayment}
                disabled={deletePaymentSubmitting || (deletePaymentReason || "").trim().length < 10}
                data-testid="confirm-delete-payment-btn"
              >
                {deletePaymentSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar pago
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assignment Dialog */}
        <Dialog open={assignmentDialogOpen} onOpenChange={setAssignmentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Asignar Unidad y Chofer</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAssignment} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="vehicle">Unidad</Label>
                <Select
                  value={assignmentData.vehicle_id}
                  onValueChange={(value) => setAssignmentData(prev => ({ ...prev, vehicle_id: value }))}
                >
                  <SelectTrigger data-testid="vehicle-select">
                    <SelectValue placeholder="Seleccionar unidad..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getVehiclesForForm(assignmentData.vehicle_id).map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {vehicleSelectLabel(v)} ({v.capacity} pax)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver">Chofer</Label>
                <Select
                  value={assignmentData.driver_id}
                  onValueChange={(value) => setAssignmentData(prev => ({ ...prev, driver_id: value }))}
                >
                  <SelectTrigger data-testid="driver-select">
                    <SelectValue placeholder="Seleccionar chofer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getDriversForForm(assignmentData.driver_id).map(d => (
                      <SelectItem key={d.id} value={d.id}>
                        {driverSelectLabel(d)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="traffic_light">Semáforo</Label>
                <Select
                  value={assignmentData.traffic_light}
                  onValueChange={(value) => setAssignmentData(prev => ({ ...prev, traffic_light: value }))}
                >
                  <SelectTrigger data-testid="traffic-light-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rojo">🔴 Rojo - Sin asignación</SelectItem>
                    <SelectItem value="amarillo">🟡 Amarillo - Asignación inicial</SelectItem>
                    <SelectItem value="verde">🟢 Verde - Definitivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isContractPostService() && (
                <div className="space-y-2 p-3 bg-amber-50 border border-amber-300 rounded-md" data-testid="post-service-section">
                  <p className="text-xs text-amber-800 font-medium flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>⚠️ Servicio ya finalizado — Documenta el motivo del cambio para auditoría</span>
                  </p>
                  <Textarea
                    value={assignmentReason}
                    onChange={(e) => setAssignmentReason(e.target.value)}
                    placeholder="Describe el motivo de esta modificación post-servicio..."
                    rows={2}
                    className="border-amber-300 focus:border-amber-500"
                    data-testid="post-service-reason-input"
                  />
                  <p className="text-[11px] text-amber-700">
                    {assignmentReason.trim().length} / 10 caracteres mínimos
                  </p>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAssignmentDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="submit-assignment-btn"
                  disabled={isContractPostService() && assignmentReason.trim().length < 10}
                >
                  Guardar Asignación
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Traffic Light Dialog (for vendedor) */}
        <Dialog open={trafficDialogOpen} onOpenChange={setTrafficDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Cambiar Semáforo</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleTrafficLightChange} className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{contract?.client_name}</p>
                <p className="text-sm text-slate-600">
                  {contract?.origin} → {contract?.destination}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Nuevo Estado del Semáforo</Label>
                <Select
                  value={newTrafficLight}
                  onValueChange={setNewTrafficLight}
                >
                  <SelectTrigger data-testid="new-traffic-light-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rojo">🔴 Rojo - Sin asignación</SelectItem>
                    <SelectItem value="amarillo">🟡 Amarillo - Asignación inicial</SelectItem>
                    <SelectItem value="verde">🟢 Verde - Definitivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isContractPostService() && (
                <div className="space-y-2 p-3 bg-amber-50 border border-amber-300 rounded-md" data-testid="post-service-section-traffic">
                  <p className="text-xs text-amber-800 font-medium flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>⚠️ Servicio ya finalizado — Documenta el motivo del cambio para auditoría</span>
                  </p>
                  <Textarea
                    value={trafficReason}
                    onChange={(e) => setTrafficReason(e.target.value)}
                    placeholder="Describe el motivo de esta modificación post-servicio..."
                    rows={2}
                    className="border-amber-300 focus:border-amber-500"
                    data-testid="post-service-reason-input-traffic"
                  />
                  <p className="text-[11px] text-amber-700">
                    {trafficReason.trim().length} / 10 caracteres mínimos
                  </p>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTrafficDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="submit-traffic-btn"
                  disabled={isContractPostService() && trafficReason.trim().length < 10}
                >
                  Actualizar Semáforo
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reassign Contract Dialog (Admin only) */}
        <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <UserCheck className="w-5 h-5" />
                Reasignar Contrato
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleReassign} className="space-y-4">
              <div className="w-full max-w-full overflow-hidden p-4 bg-slate-50 rounded-lg">
                <p className="font-medium break-words">{contract?.client_name}</p>
                <p className="text-sm text-slate-600 break-words">
                  Folio: {contract?.folio} | {contract?.origin} → {contract?.destination}
                </p>
                {salesOwnerUser && (
                  <p className="text-sm text-slate-500 mt-2 break-words">
                    Vendedor actual: <span className="font-medium">{salesOwnerUser.name}</span>
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_owner">Nuevo Vendedor *</Label>
                <Select
                  value={reassignData.new_sales_owner_id}
                  onValueChange={(value) => setReassignData(prev => ({ ...prev, new_sales_owner_id: value }))}
                >
                  <SelectTrigger data-testid="reassign-owner-select">
                    <SelectValue placeholder="Seleccionar vendedor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allUsers
                      .filter(u => u.role === "vendedor" || u.role === "admin")
                      .map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} ({u.email}) - {u.role}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reassign_reason">Razón de reasignación *</Label>
                <Textarea
                  id="reassign_reason"
                  placeholder="Ej: El cliente prefiere tratar con otro vendedor, cambio de territorio, etc."
                  value={reassignData.reason}
                  onChange={(e) => setReassignData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  data-testid="reassign-reason-input"
                />
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Esta acción transferirá la propiedad del contrato al vendedor seleccionado. 
                El cambio quedará registrado en la bitácora.
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setReassignDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={reassignLoading} data-testid="submit-reassign-btn">
                  {reassignLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Reasignando...
                    </>
                  ) : (
                    "Reasignar Contrato"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Complete Contract Confirmation Dialog */}
        <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Check className="w-5 h-5" />
                Confirmar Completado
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{contract?.client_name}</p>
                <p className="text-sm text-slate-600">
                  Folio: {contract?.folio} | {contract?.origin} &rarr; {contract?.destination}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Fecha servicio: {formatDate(contract?.pickup_datetime)}
                </p>
              </div>

              {/* Warnings for missing assignments */}
              {getCompletionWarnings().length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1" data-testid="completion-warnings">
                  {getCompletionWarnings().map((w, i) => (
                    <p key={i} className="text-sm text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      {w}
                    </p>
                  ))}
                </div>
              )}

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                Al marcar como completado, este contrato dejara de aparecer en el modulo de Asignacion.
              </div>

              <div className="space-y-2">
                <Label htmlFor="completion_note">Comentario de cierre (opcional)</Label>
                <Textarea
                  id="completion_note"
                  placeholder="Ej: Servicio realizado sin novedades, cliente satisfecho..."
                  value={completionNote}
                  onChange={(e) => setCompletionNote(e.target.value)}
                  rows={2}
                  data-testid="completion-note-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCompleteDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleConfirmComplete} data-testid="confirm-complete-btn">
                <Check className="w-4 h-4 mr-2" />
                Confirmar Completado
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Stripe Custom Amount Dialog */}
        <Dialog open={stripeDialogOpen} onOpenChange={setStripeDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Generar Link de Pago Stripe
              </DialogTitle>
            </DialogHeader>
            
            {paymentSummary && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Saldo pendiente:</span>
                  <span className="font-medium">${paymentSummary.balance?.toLocaleString("es-MX")}</span>
                </div>
                {!paymentSummary.deposit_met && (
                  <div className="flex justify-between text-amber-600">
                    <span>Anticipo pendiente (20%):</span>
                    <span className="font-medium">
                      ${(paymentSummary.deposit_required - paymentSummary.total_paid)?.toLocaleString("es-MX")}
                    </span>
                  </div>
                )}
              </div>
            )}
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="stripeAmount">Monto a cobrar *</Label>
                <Input
                  id="stripeAmount"
                  type="number"
                  step="any"
                  min="1"
                  max={paymentSummary?.balance || 999999}
                  value={customStripeAmount}
                  onChange={(e) => setCustomStripeAmount(parseFloat(e.target.value) || 0)}
                  data-testid="stripe-amount-input"
                />
                <p className="text-xs text-slate-500">
                  Moneda: {contract?.currency || "MXN"}
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setStripeDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={() => handleStripePayment("custom", customStripeAmount)}
                disabled={stripeLoading || customStripeAmount <= 0}
                data-testid="generate-stripe-link-btn"
              >
                {stripeLoading ? "Generando..." : "Generar Link de Pago"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Logistics Confirmation Dialog */}
        <Dialog open={logisticsConfirmOpen} onOpenChange={setLogisticsConfirmOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Truck className="w-5 h-5 text-purple-500" />
                Confirmación de Cambio Operativo
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
                <p className="font-medium">⚠️ Este contrato ya tiene asignación logística (unidad/chofer).</p>
                <p className="mt-2">Estos cambios pueden afectar la operación programada. Por favor confirme que desea continuar.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="operational_change_reason">Razón del cambio operativo * (mín. 10 caracteres)</Label>
                <Textarea
                  id="operational_change_reason"
                  value={operationalChangeReason}
                  onChange={(e) => setOperationalChangeReason(e.target.value)}
                  placeholder="Ej: Cliente solicita cambio de fecha, ajuste de ruta por cierre vial, etc."
                  rows={3}
                  data-testid="operational-change-reason-input"
                />
                <p className="text-xs text-slate-500">
                  {operationalChangeReason.length}/10 caracteres mínimos
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setLogisticsConfirmOpen(false);
                setOperationalChangeReason("");
                setPendingChanges(null);
              }}>
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmLogisticsChange}
                disabled={operationalChangeReason.trim().length < 10 || editLoading}
                data-testid="confirm-logistics-change-btn"
              >
                {editLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Confirmar Cambio
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Reason Dialog */}
        <Dialog open={changeReasonDialogOpen} onOpenChange={setChangeReasonDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Razón del Cambio Requerida
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <p>Está modificando campos financieros del contrato. Por políticas de auditoría, debe proporcionar una razón para este cambio.</p>
                {hasPayments && (
                  <p className="mt-2 font-medium">⚠️ Este contrato tiene pagos registrados. Algunos cambios pueden estar restringidos.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="change_reason">Razón del cambio *</Label>
                <Textarea
                  id="change_reason"
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  placeholder="Ej: Ajuste de precio por solicitud del cliente, corrección de error de captura, etc."
                  rows={3}
                  data-testid="change-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setChangeReasonDialogOpen(false);
                setChangeReason("");
                setPendingChanges(null);
              }}>
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmChangeReason}
                disabled={!changeReason.trim() || editLoading}
                data-testid="confirm-change-reason-btn"
              >
                {editLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Confirmar Cambio
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Service Orders by Unit Dialog */}
        <Dialog open={serviceOrdersDialogOpen} onOpenChange={setServiceOrdersDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Órdenes de Servicio por Unidad
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <div className="text-sm text-slate-500 mb-4 p-3 bg-slate-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span>Contrato: <span className="font-mono font-medium text-slate-700">{contract?.folio}</span></span>
                  <Badge>{serviceOrders.length} {serviceOrders.length === 1 ? 'unidad' : 'unidades'}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-400">Origen:</span> {contract?.origin}
                  </div>
                  <div>
                    <span className="text-slate-400">Destino:</span> {contract?.destination}
                  </div>
                </div>
                {/* Full Itinerary */}
                {contract?.full_itinerary && (
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <Route className="w-3 h-3" />
                      Itinerario Completo:
                    </p>
                    <p className="text-xs text-slate-600 whitespace-pre-wrap mt-1">{contract.full_itinerary}</p>
                  </div>
                )}
                {/* Special Requirements */}
                {contract?.special_requirements && (
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Requerimientos Especiales:
                    </p>
                    <p className="text-xs text-amber-700 mt-1">{contract.special_requirements}</p>
                  </div>
                )}
              </div>
              
              {serviceOrders.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay unidades en este contrato</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {serviceOrders.map((order) => (
                    <div 
                      key={order.unit_id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {order.label}
                          </Badge>
                          <span className="font-medium text-slate-700">
                            {getVehicleTypeLabel(order.vehicle_type)}
                          </span>
                        </div>
                        <div className="text-sm text-slate-500">
                          Capacidad: {order.capacity} pax
                        </div>
                        <div className="text-sm">
                          <span className="text-slate-500">Precio: </span>
                          <span className="font-medium">${order.base_price?.toLocaleString("es-MX")} {contract?.currency || "MXN"}</span>
                          {contract?.requires_invoice && order.vat_amount > 0 && (
                            <span className="text-slate-500 ml-2">
                              + IVA ${order.vat_amount?.toLocaleString("es-MX")} = 
                              <span className="font-medium text-green-600 ml-1">
                                ${order.total_with_vat?.toLocaleString("es-MX")}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDownloadUnitServiceOrderPDF(order.unit_id, order.label)}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Descargar PDF
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-4 pt-4 border-t flex justify-between items-center">
                <div className="text-sm text-slate-500">
                  Total del contrato: 
                  <span className="font-medium text-slate-700 ml-1">
                    ${contract?.total_price?.toLocaleString("es-MX")} {contract?.currency || "MXN"}
                  </span>
                </div>
                <Button 
                  variant="outline"
                  onClick={handleDownloadServiceOrderPDF}
                  disabled={serviceOrderLoading}
                  className="gap-2"
                >
                  {serviceOrderLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Descargar Todas (PDF único)
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      {/* P2: Client Data Edit Dialog */}
      <Dialog open={clientEditDialogOpen} onOpenChange={(open) => { setClientEditDialogOpen(open); if (!open) setShowClientEditHistory(false); }}>
        <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Datos Contractuales del Cliente</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Estos cambios afectan solo este contrato. No modifica el cliente base del CRM.
            </p>
          </DialogHeader>
          
          {!showClientEditHistory ? (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-sm">Nombre del Cliente *</Label>
                <Input
                  value={clientEditData.client_name || ""}
                  onChange={(e) => setClientEditData({...clientEditData, client_name: e.target.value})}
                  data-testid="p2-client-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Teléfono</Label>
                  <Input
                    value={clientEditData.client_phone || ""}
                    onChange={(e) => setClientEditData({...clientEditData, client_phone: e.target.value})}
                    data-testid="p2-client-phone"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Email</Label>
                  <Input
                    value={clientEditData.client_email || ""}
                    onChange={(e) => setClientEditData({...clientEditData, client_email: e.target.value})}
                    data-testid="p2-client-email"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">RFC</Label>
                  <Input
                    value={clientEditData.client_rfc || ""}
                    onChange={(e) => setClientEditData({...clientEditData, client_rfc: e.target.value})}
                    data-testid="p2-client-rfc"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Razón Social</Label>
                  <Input
                    value={clientEditData.client_legal_name || ""}
                    onChange={(e) => setClientEditData({...clientEditData, client_legal_name: e.target.value})}
                    data-testid="p2-client-legal-name"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Domicilio Fiscal</Label>
                <Input
                  value={clientEditData.client_address || ""}
                  onChange={(e) => setClientEditData({...clientEditData, client_address: e.target.value})}
                  data-testid="p2-client-address"
                />
              </div>
              <div className="space-y-1.5 pt-3 border-t">
                <Label className="text-sm text-red-600">Motivo de la edición *</Label>
                <Textarea
                  value={clientEditReason}
                  onChange={(e) => setClientEditReason(e.target.value)}
                  placeholder="Describa el motivo del cambio (obligatorio para auditoría)..."
                  className={!clientEditReason.trim() ? "border-red-300" : ""}
                  data-testid="p2-edit-reason"
                />
              </div>
              {clientEditHistory.length > 0 && (
                <Button variant="link" size="sm" className="text-xs p-0 h-auto text-slate-500" onClick={() => setShowClientEditHistory(true)}>
                  Ver historial de ediciones ({clientEditHistory.length})
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-slate-700">Historial de Ediciones</h4>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowClientEditHistory(false)}>
                  Volver al formulario
                </Button>
              </div>
              {clientEditHistory.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">Sin ediciones previas</p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {clientEditHistory.map((edit, idx) => (
                    <div key={edit.id || idx} className="p-3 bg-slate-50 rounded-lg border text-xs" data-testid={`edit-history-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-700">{edit.edited_by_name || "Admin"}</span>
                        <span className="text-slate-400">{new Date(edit.edited_at).toLocaleString("es-MX")}</span>
                      </div>
                      <p className="text-slate-600 mb-2 italic">"{edit.edit_reason}"</p>
                      <div className="space-y-1">
                        {(edit.field_changes || []).map((ch, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="font-mono text-slate-500 w-28 shrink-0">{ch.field}:</span>
                            <span className="text-red-500 line-through">{ch.old_value || "(vacío)"}</span>
                            <span className="text-slate-300">→</span>
                            <span className="text-green-700 font-medium">{ch.new_value || "(vacío)"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {!showClientEditHistory && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setClientEditDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveClientEdit} disabled={clientEditLoading || !clientEditReason.trim()} data-testid="p2-save-btn">
                {clientEditLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Guardar Cambios
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      </div>

      {/* Flexible Payment Link Dialog (Fase Ligas) */}
      <PaymentLinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        contractId={id}
        onLinkCreated={fetchPaymentLinks}
      />
    </Layout>
  );
}

// Export con Error Boundary Global
export default function ContratoDetalle() {
  return (
    <GlobalErrorBoundary>
      <ContratoDetalleContent />
    </GlobalErrorBoundary>
  );
}
